import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { Button } from './ui/button';
import AffiliateManagement from './AffiliateManagement';
import FaqBotTab from './admin/FaqBotTab';
import ChatHistoryTab from './admin/ChatHistoryTab';
import PricingTab from './admin/PricingTab';
import AnalyticsTab from './admin/AnalyticsTab';
import MaintenanceToggle from './admin/MaintenanceToggle';
import AqmOptiToolTab from './admin/AqmOptiToolTab';
import PollsTab from './admin/PollsTab';
import SiteStatsTab from './admin/SiteStatsTab';
import ReferralTestTab from './admin/ReferralTestTab';
import { Bot as BotIcon, BarChart3 as BarChartIcon, Gift as GiftIcon } from 'lucide-react';
import { 
  Users, Package, Clock, CheckCircle, Check,
  XCircle, Trash2, AlertCircle, Shield,
  Search, Bell, Plus, Megaphone, Tag, Eye, EyeOff, Gift, TrendingUp, Ban, RotateCcw, Activity, Wifi, Zap, Percent, Timer,
  Copy, X as XIcon, DollarSign, Image as ImageIcon, FileText, Link as LinkIcon, Briefcase, Wrench, MessageSquare, ChevronDown, ChevronUp, ExternalLink,
  Lightbulb, Globe, Cpu, MoreHorizontal, UserCog, Crown, Star, Terminal, Edit3
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Voice message player for admin: lazy-loads audio on demand + speed control
const VoiceMessagePlayer = ({ appId, duration, token }) => {
  const [audio, setAudio] = useState(null); // { src, mime }
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [current, setCurrent] = useState(0);
  const audioRef = React.useRef(null);

  const load = async () => {
    if (audio) return;
    setLoading(true); setErr(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/helper-applications/${appId}/voice`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Échec du chargement');
      const data = await res.json();
      setAudio({ src: data.data_uri, mime: data.mime });
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = speed;
  }, [speed, audio]);

  const toggle = async () => {
    if (!audio) {
      await load();
      // wait next tick then play
      setTimeout(() => { audioRef.current && audioRef.current.play(); }, 60);
      return;
    }
    if (audioRef.current.paused) audioRef.current.play();
    else audioRef.current.pause();
  };

  const fmt = (s) => `${Math.floor(s/60).toString().padStart(2,'0')}:${Math.floor(s%60).toString().padStart(2,'0')}`;
  const total = duration || 0;
  const progress = total > 0 ? Math.min(100, (current / total) * 100) : 0;

  return (
    <div className="bg-purple-500/5 border border-purple-500/20 rounded-lg p-3 mb-4" data-testid={`voice-player-${appId}`}>
      <div className="flex items-center gap-3">
        <button
          onClick={toggle}
          disabled={loading}
          className="flex-shrink-0 w-9 h-9 rounded-full bg-purple-500 hover:bg-purple-400 disabled:opacity-50 text-white flex items-center justify-center transition-colors"
          data-testid={`voice-toggle-${appId}`}
        >
          {loading
            ? <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            : playing ? <span className="text-xs">⏸</span> : <span className="text-xs ml-0.5">▶</span>}
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-purple-300/80 text-xs font-medium">🎤 Message vocal</p>
            <span className="text-purple-300/60 text-[10px] font-mono">{fmt(current)} / {fmt(total)}</span>
          </div>
          <div className="w-full bg-purple-900/40 rounded-full h-1">
            <div
              className="h-1 bg-purple-400 rounded-full transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {audio && (
          <div className="flex items-center gap-1 flex-shrink-0">
            {[1, 1.5, 2].map((s) => (
              <button
                key={s}
                onClick={() => setSpeed(s)}
                className={`px-1.5 py-0.5 rounded text-[10px] font-bold transition-colors ${
                  speed === s ? 'bg-purple-500 text-white' : 'bg-purple-500/15 text-purple-300 hover:bg-purple-500/25'
                }`}
                data-testid={`voice-speed-${s}-${appId}`}
              >
                {s}x
              </button>
            ))}
          </div>
        )}
      </div>
      {audio && (
        <audio
          ref={audioRef}
          src={audio.src}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => { setPlaying(false); setCurrent(0); }}
          onTimeUpdate={(e) => setCurrent(e.target.currentTime)}
          preload="metadata"
        />
      )}
      {err && <p className="mt-2 text-red-400 text-xs">{err}</p>}
    </div>
  );
};

// Small toggle switch used in recruitment settings
const ToggleRow = ({ label, checked, onChange, dataTestId }) => (
  <label className="flex items-center justify-between cursor-pointer group">
    <span className="text-xs text-zinc-300">{label}</span>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={onChange}
      data-testid={dataTestId}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
        checked ? 'bg-cyan-500' : 'bg-zinc-700'
      }`}
    >
      <span
        className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform shadow ${
          checked ? 'translate-x-5' : 'translate-x-1'
        }`}
      />
    </button>
  </label>
);

const AdminPage = () => {
  const { isAuthenticated, token, user } = useAuth();
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [helperApplications, setHelperApplications] = useState([]);
  const [payoutRequests, setPayoutRequests] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionFilter, setSuggestionFilter] = useState('all'); // all | new | seen | done | <category>
  const [helperLoading, setHelperLoading] = useState(false);
  const [helperNotes, setHelperNotes] = useState({});
  const [helperMessages, setHelperMessages] = useState({});
  const [sendingCandidateMsg, setSendingCandidateMsg] = useState(null);
  const [recruitmentSettings, setRecruitmentSettings] = useState({
    helper: { open: true, visible: true },
    community_manager: { open: true, visible: true },
    tester: { open: true, visible: true },
    content_creator: { open: true, visible: true },
  });
  const [savingRecruitmentSettings, setSavingRecruitmentSettings] = useState(false);
  const [supportTickets, setSupportTickets] = useState([]);
  const [supportLoading, setSupportLoading] = useState(false);
  const [supportNotes, setSupportNotes] = useState({});
  const [expandedTicket, setExpandedTicket] = useState(null);
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [referralStats, setReferralStats] = useState([]);
  const [affiliateStats, setAffiliateStats] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Monthly revenue recap state (for CA declaration)
  const [revenueYear, setRevenueYear] = useState(new Date().getFullYear());
  const [revenueData, setRevenueData] = useState(null);
  const [revenueLoading, setRevenueLoading] = useState(false);
  // Daily revenue recap (CA par jour) state
  const [dailyMonth, setDailyMonth] = useState(new Date().getMonth() + 1);
  const [dailyData, setDailyData] = useState(null);
  const [dailyLoading, setDailyLoading] = useState(false);
  
  // Search states for users and bans
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [banSearchTerm, setBanSearchTerm] = useState('');
  
  // Active users state
  const [activeUsers, setActiveUsers] = useState({ count: 0, users: [], anonymous_count: 0, anonymous: [], total_count: 0 });
  
  // IP Ban state
  const [bannedIPs, setBannedIPs] = useState([]);
  const [bannedUsers, setBannedUsers] = useState([]);
  const [newBanIP, setNewBanIP] = useState('');
  const [newBanReason, setNewBanReason] = useState('');
  const [banLoading, setBanLoading] = useState(false);
  const [visitorIP, setVisitorIP] = useState('');
  
  // New announcement form
  const [showNewAnnouncement, setShowNewAnnouncement] = useState(false);
  const [newAnnouncement, setNewAnnouncement] = useState({
    title: '',
    content: '',
    type: 'info'
  });
  const [announcementLoading, setAnnouncementLoading] = useState(false);
  
  // Custom commands state
  const [customCommands, setCustomCommands] = useState([]);
  const [showNewCommand, setShowNewCommand] = useState(false);
  const [newCommand, setNewCommand] = useState({
    name: '',
    description: '',
    embed_title: '',
    embed_content: '',
    embed_color: '3498DB'
  });
  const [commandLoading, setCommandLoading] = useState(false);
  
  // Promo codes state
  const [promoCodes, setPromoCodes] = useState([]);
  const [showNewPromo, setShowNewPromo] = useState(false);
  const [newPromo, setNewPromo] = useState({
    code: '',
    discount_percent: 10,
    max_uses: '',
    valid_until: '',
    services: null  // null = all services
  });
  const [promoLoading, setPromoLoading] = useState(false);
  
  // Flash sale state
  const [flashSale, setFlashSale] = useState(null);
  const [showNewFlashSale, setShowNewFlashSale] = useState(false);
  const [newFlashSale, setNewFlashSale] = useState({
    title: 'Vente Flash',
    discount_percent: 20,
    duration_hours: 24,
    services: null,
    live: false,
    tiktok_url: '',
    banner_text: '',
  });
  const [flashSaleLoading, setFlashSaleLoading] = useState(false);
  
  // User affiliates state
  const [userAffiliates, setUserAffiliates] = useState([]);
  const [affiliateSearchTerm, setAffiliateSearchTerm] = useState('');
  const [affiliateLoading, setAffiliateLoading] = useState({});

  // Pay affiliate modal state
  const [payModal, setPayModal] = useState(null); // { userId, username, amount, affiliateCode }
  const [payProofFile, setPayProofFile] = useState(null);
  const [payProofPreview, setPayProofPreview] = useState('');
  const [payProofNote, setPayProofNote] = useState('');
  const [paySubmitting, setPaySubmitting] = useState(false);

  // Affiliate details modal (admin side)
  const [detailsModal, setDetailsModal] = useState(null); // affiliate details object
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsProof, setDetailsProof] = useState(null); // { id, image_base64, content_type, note, created_at, uploaded_by }
  const [detailsProofLoadingId, setDetailsProofLoadingId] = useState(null);
  const [detailsCopied, setDetailsCopied] = useState(false);
  const [editingLink, setEditingLink] = useState(false);
  const [newLinkCode, setNewLinkCode] = useState('');
  const [savingLink, setSavingLink] = useState(false);
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/');
      return;
    }
    checkAdmin();
  }, [isAuthenticated]);

  const checkAdmin = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/check`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.is_admin) {
        setIsAdmin(true);
        fetchData();
      } else {
        navigate('/');
      }
    } catch (error) {
      console.error('Admin check failed:', error);
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      const [statsRes, ordersRes, usersRes, announcementsRes, commandsRes, referralsRes, affiliatesRes, activeUsersRes, promoRes, flashRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/stats`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/admin/orders`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/admin/users`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/admin/announcements`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/admin/commands`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/admin/referrals`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/admin/affiliates`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/admin/active-users`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/promo-codes`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/flash-sale`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      
      setStats(await statsRes.json());
      setOrders(await ordersRes.json());
      setUsers(await usersRes.json());
      setAnnouncements(await announcementsRes.json());
      if (commandsRes.ok) {
        setCustomCommands(await commandsRes.json());
      }
      if (referralsRes.ok) {
        setReferralStats(await referralsRes.json());
      }
      if (affiliatesRes.ok) {
        const affiliatesData = await affiliatesRes.json();
        setAffiliateStats(affiliatesData);
      }
      if (activeUsersRes.ok) {
        setActiveUsers(await activeUsersRes.json());
      }
      if (promoRes.ok) {
        setPromoCodes(await promoRes.json());
      }
      if (flashRes.ok) {
        const flashData = await flashRes.json();
        setFlashSale(flashData.active ? flashData : null);
      }
      
      // Fetch user affiliates
      const userAffiliatesRes = await fetch(`${API_URL}/api/admin/user-affiliates`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (userAffiliatesRes.ok) {
        setUserAffiliates(await userAffiliatesRes.json());
      }
      
      // Fetch helper applications
      const helperRes = await fetch(`${API_URL}/api/admin/helper-applications`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (helperRes.ok) {
        setHelperApplications(await helperRes.json());
      }

      // Fetch affiliate payout requests
      const payoutRes = await fetch(`${API_URL}/api/admin/affiliate-payout-requests`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (payoutRes.ok) {
        setPayoutRequests(await payoutRes.json());
      }

      // Fetch suggestions
      const suggRes = await fetch(`${API_URL}/api/admin/suggestions`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (suggRes.ok) {
        setSuggestions(await suggRes.json());
      }
    } catch (error) {
      console.error('Error fetching admin data:', error);
    }
  };

  // Fetch monthly revenue recap when on orders tab or year changes
  useEffect(() => {
    if (!isAdmin || !token || activeTab !== 'orders') return;
    let cancelled = false;
    const fetchRevenue = async () => {
      try {
        setRevenueLoading(true);
        const res = await fetch(`${API_URL}/api/admin/orders/monthly-revenue?year=${revenueYear}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok && !cancelled) {
          setRevenueData(await res.json());
        }
      } catch (error) {
        console.error('Error fetching monthly revenue:', error);
      } finally {
        if (!cancelled) setRevenueLoading(false);
      }
    };
    fetchRevenue();
    return () => { cancelled = true; };
  }, [isAdmin, token, activeTab, revenueYear, API_URL]);

  // Fetch daily revenue recap when on orders tab or year/month changes
  useEffect(() => {
    if (!isAdmin || !token || activeTab !== 'orders') return;
    let cancelled = false;
    const fetchDaily = async () => {
      try {
        setDailyLoading(true);
        const res = await fetch(`${API_URL}/api/admin/orders/daily-revenue?year=${revenueYear}&month=${dailyMonth}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok && !cancelled) {
          setDailyData(await res.json());
        }
      } catch (error) {
        console.error('Error fetching daily revenue:', error);
      } finally {
        if (!cancelled) setDailyLoading(false);
      }
    };
    fetchDaily();
    return () => { cancelled = true; };
  }, [isAdmin, token, activeTab, revenueYear, dailyMonth, API_URL]);

  // Fetch active users every 10 seconds for real-time updates
  useEffect(() => {
    if (!isAdmin || !token) return;
    
    const fetchActiveUsers = async () => {
      try {
        const res = await fetch(`${API_URL}/api/admin/active-users`, { 
          headers: { 'Authorization': `Bearer ${token}` } 
        });
        if (res.ok) {
          setActiveUsers(await res.json());
        }
      } catch (error) {
        console.error('Error fetching active users:', error);
      }
    };

    const interval = setInterval(fetchActiveUsers, 10000);
    return () => clearInterval(interval);
  }, [isAdmin, token]);

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await fetch(`${API_URL}/api/admin/orders/${orderId}/status?status=${newStatus}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      console.error('Error updating order:', error);
    }
  };

  const deleteOrder = async (orderId) => {
    if (!window.confirm('Supprimer cette commande ?')) return;
    
    try {
      await fetch(`${API_URL}/api/admin/orders/${orderId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      console.error('Error deleting order:', error);
    }
  };

  const toggleOrderRefund = async (orderId, currentlyRefunded) => {
    const action = currentlyRefunded ? 'annuler le remboursement de' : 'marquer comme remboursée';
    if (!window.confirm(`Voulez-vous vraiment ${action} cette commande ?`)) return;
    try {
      await fetch(`${API_URL}/api/admin/orders/${orderId}/refund?refunded=${!currentlyRefunded}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      // Refresh both the orders list AND the revenue recap
      await fetchData();
      setRevenueYear((y) => y); // trigger refetch
      const res = await fetch(`${API_URL}/api/admin/orders/monthly-revenue?year=${revenueYear}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setRevenueData(await res.json());
    } catch (error) {
      console.error('Error toggling refund:', error);
    }
  };

  const updateOrderPrice = async (orderId, currentPrice) => {
    const input = window.prompt('Nouveau prix (€) :', currentPrice ?? '0');
    if (input === null) return;
    const value = parseFloat(String(input).replace(',', '.'));
    if (Number.isNaN(value) || value < 0) {
      alert('Prix invalide');
      return;
    }
    try {
      await fetch(`${API_URL}/api/admin/orders/${orderId}/price?price=${value}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      await fetchData();
      const res = await fetch(`${API_URL}/api/admin/orders/monthly-revenue?year=${revenueYear}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setRevenueData(await res.json());
    } catch (error) {
      console.error('Error updating price:', error);
    }
  };

  const createAnnouncement = async () => {
    if (!newAnnouncement.title || !newAnnouncement.content) return;
    
    setAnnouncementLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/announcements`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newAnnouncement)
      });
      
      if (response.ok) {
        setNewAnnouncement({ title: '', content: '', type: 'info' });
        setShowNewAnnouncement(false);
        fetchData();
      }
    } catch (error) {
      console.error('Error creating announcement:', error);
    } finally {
      setAnnouncementLoading(false);
    }
  };

  const toggleAnnouncement = async (id) => {
    try {
      await fetch(`${API_URL}/api/admin/announcements/${id}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      console.error('Error toggling announcement:', error);
    }
  };

  const deleteAnnouncement = async (id) => {
    if (!window.confirm('Supprimer cette annonce ?')) return;
    
    try {
      await fetch(`${API_URL}/api/admin/announcements/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      console.error('Error deleting announcement:', error);
    }
  };

  // Custom Commands Functions
  const createCommand = async () => {
    if (!newCommand.name || !newCommand.embed_title || !newCommand.embed_content) return;
    
    setCommandLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/commands`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newCommand)
      });
      
      if (response.ok) {
        setNewCommand({ name: '', description: '', embed_title: '', embed_content: '', embed_color: '3498DB' });
        setShowNewCommand(false);
        fetchData();
      }
    } catch (error) {
      console.error('Error creating command:', error);
    } finally {
      setCommandLoading(false);
    }
  };

  const deleteCommand = async (name) => {
    if (!window.confirm(`Supprimer la commande *${name} ?`)) return;
    
    try {
      await fetch(`${API_URL}/api/admin/commands/${name}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      console.error('Error deleting command:', error);



    }
  };

  // IP Ban Functions
  const fetchBannedIPs = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/banned-ips`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setBannedIPs(await response.json());
      }
    } catch (error) {
      console.error('Error fetching banned IPs:', error);
    }
  };

  const fetchBannedUsers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/banned-users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setBannedUsers(await response.json());
      }
    } catch (error) {
      console.error('Error fetching banned users:', error);
    }
  };

  const fetchVisitorIP = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/visitor-ip`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setVisitorIP(data.ip);
      }
    } catch (error) {
      console.error('Error fetching visitor IP:', error);
    }
  };

  const banIP = async () => {
    if (!newBanIP.trim()) return;
    
    setBanLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/ban-ip`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          ip: newBanIP.trim(), 
          reason: newBanReason.trim() || 'Non spécifié' 
        })
      });
      
      if (response.ok) {
        setNewBanIP('');
        setNewBanReason('');
        fetchBannedIPs();
      } else {
        const error = await response.json();
        alert(error.detail || 'Erreur lors du bannissement');
      }
    } catch (error) {
      console.error('Error banning IP:', error);
      alert('Erreur lors du bannissement');
    } finally {
      setBanLoading(false);
    }
  };

  const banUser = async (userId, username) => {
    const reason = window.prompt(`Raison du bannissement de ${username} :`, 'Non spécifié');
    if (reason === null) return; // User cancelled
    
    try {
      const response = await fetch(`${API_URL}/api/admin/ban-user/${userId}`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ user_id: userId, reason: reason || 'Non spécifié' })
      });
      
      if (response.ok) {
        alert(`${username} a été banni du site avec succès`);
        fetchData(); // Refresh users list
        fetchBannedIPs(); // Refresh banned IP list
        fetchBannedUsers(); // Refresh banned users list
      } else {
        const error = await response.json();
        alert(error.detail || 'Erreur lors du bannissement');
      }
    } catch (error) {
      console.error('Error banning user:', error);
      alert('Erreur lors du bannissement');
    }
  };

  const unbanUser = async (discordId, username) => {
    if (!window.confirm(`Débannir ${username || discordId} du site ?`)) return;
    try {
      const response = await fetch(`${API_URL}/api/admin/unban-user/${encodeURIComponent(discordId)}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchData();
        fetchBannedIPs();
        fetchBannedUsers();
      } else {
        const error = await response.json();
        alert(error.detail || 'Erreur lors du débannissement');
      }
    } catch (error) {
      console.error('Error unbanning user:', error);
    }
  };

  const unbanIP = async (ip) => {
    if (!window.confirm(`Débannir l'IP ${ip} ?`)) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/unban-ip/${encodeURIComponent(ip)}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        fetchBannedIPs();
      } else {
        const error = await response.json();
        alert(error.detail || 'Erreur lors du débannissement');
      }
    } catch (error) {
      console.error('Error unbanning IP:', error);
    }
  };

  // Check if user is banned from the site (by Discord ID)
  const isUserBanned = (discordId) => {
    return bannedUsers.some(ban => String(ban.discord_id) === String(discordId));
  };

  // Fetch banned lists when bans tab is active or users tab
  useEffect(() => {
    if ((activeTab === 'bans' || activeTab === 'users') && isAdmin) {
      fetchBannedIPs();
      fetchBannedUsers();
      if (activeTab === 'bans') {
        fetchVisitorIP();
      }
    }
  }, [activeTab, isAdmin]);

  // Promo code functions
  const createPromoCode = async (e) => {
    e.preventDefault();
    setPromoLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/promo-codes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          code: newPromo.code.toUpperCase(),
          discount_percent: parseInt(newPromo.discount_percent),
          max_uses: newPromo.max_uses ? parseInt(newPromo.max_uses) : null,
          valid_until: newPromo.valid_until || null,
          services: newPromo.services && newPromo.services.length > 0 ? newPromo.services : null
        })
      });
      
      if (response.ok) {
        setShowNewPromo(false);
        setNewPromo({ code: '', discount_percent: 10, max_uses: '', valid_until: '', services: null });
        fetchData();
      } else {
        const error = await response.json();
        alert(error.detail || 'Erreur lors de la création du code promo');
      }
    } catch (error) {
      console.error('Error creating promo code:', error);
      alert('Erreur lors de la création');
    } finally {
      setPromoLoading(false);
    }
  };

  const deletePromoCode = async (code) => {
    if (!window.confirm(`Supprimer le code promo ${code} ?`)) return;
    
    try {
      const response = await fetch(`${API_URL}/api/promo-codes/${code}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        fetchData();
      } else {
        alert('Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Error deleting promo code:', error);
    }
  };

  const togglePromoCode = async (code, currentActive) => {
    try {
      const response = await fetch(`${API_URL}/api/promo-codes/${code}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ active: !currentActive })
      });
      
      if (response.ok) {
        fetchData();
      }
    } catch (error) {
      console.error('Error toggling promo code:', error);
    }
  };

  // Flash sale functions
  const createFlashSale = async (e) => {
    e.preventDefault();
    setFlashSaleLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/flash-sale`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: newFlashSale.title,
          discount_percent: parseInt(newFlashSale.discount_percent),
          duration_hours: parseInt(newFlashSale.duration_hours),
          services: newFlashSale.services && newFlashSale.services.length > 0 ? newFlashSale.services : null,
          live: newFlashSale.live,
          tiktok_url: newFlashSale.live && newFlashSale.tiktok_url ? newFlashSale.tiktok_url : null,
          banner_text: newFlashSale.banner_text ? newFlashSale.banner_text : null
        })
      });
      
      if (response.ok) {
        setShowNewFlashSale(false);
        setNewFlashSale({ title: 'Vente Flash', discount_percent: 20, duration_hours: 24, services: null, live: false, tiktok_url: '', banner_text: '' });
        fetchData();
      } else {
        const error = await response.json();
        alert(error.detail || 'Erreur lors de la création de la vente flash');
      }
    } catch (error) {
      console.error('Error creating flash sale:', error);
      alert('Erreur lors de la création');
    } finally {
      setFlashSaleLoading(false);
    }
  };

  const endFlashSale = async () => {
    if (!flashSale || !window.confirm('Terminer la vente flash en cours ?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/flash-sale/${flashSale.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        setFlashSale(null);
        fetchData();
      }
    } catch (error) {
      console.error('Error ending flash sale:', error);
    }
  };

  // User affiliate functions
  const toggleUserAffiliate = async (userId, username, currentIsAffiliate) => {
    setAffiliateLoading(prev => ({ ...prev, [userId]: true }));
    
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}/toggle-affiliate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          is_affiliate: !currentIsAffiliate,
          commission_rate: 10.0
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.affiliate_code) {
          alert(`${username} est maintenant affilié !\nCode: ${data.affiliate_code}`);
        } else {
          alert(`${username} n'est plus affilié.`);
        }
        fetchData();
      } else {
        const error = await response.json();
        alert(error.detail || 'Erreur');
      }
    } catch (error) {
      console.error('Error toggling affiliate:', error);
      alert('Erreur lors de la modification');
    } finally {
      setAffiliateLoading(prev => ({ ...prev, [userId]: false }));
    }
  };

  const updateAffiliateRate = async (userId, newRate) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}/affiliate-rate`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ commission_rate: parseFloat(newRate) })
      });
      
      if (response.ok) {
        fetchData();
      }
    } catch (error) {
      console.error('Error updating rate:', error);
    }
  };

  const assignAffiliatePromo = async (affiliateCode) => {
    const promo_code = (window.prompt(`Code promo pour ${affiliateCode} (ex: ${affiliateCode}5) :`, `${affiliateCode}5`.toUpperCase().replace(/[^A-Z0-9]/g, '')) || '').trim();
    if (!promo_code) return;
    const discStr = window.prompt('Réduction pour le client (%) :', '5');
    if (discStr === null) return;
    const discount_percent = parseInt(discStr, 10) || 5;
    try {
      const res = await fetch(`${API_URL}/api/admin/affiliates/${affiliateCode}/promo`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ promo_code, discount_percent })
      });
      if (res.ok) {
        fetchData();
        alert(`✅ Code promo "${promo_code.toUpperCase()}" (-${discount_percent}%) attribué à ${affiliateCode}. Il apparaît maintenant sur sa page affilié.`);
      } else {
        const d = await res.json().catch(() => ({}));
        alert(d.detail || 'Erreur lors de la création du code promo');
      }
    } catch {
      alert('Erreur réseau');
    }
  };

  const removeAffiliatePromo = async (affiliateCode) => {
    if (!window.confirm(`Retirer le code promo de ${affiliateCode} ?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/affiliates/${affiliateCode}/promo`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) fetchData();
    } catch {
      alert('Erreur réseau');
    }
  };

  const payAffiliate = (userId, username, amount, affiliateCode) => {
    // Open the modal instead of immediate confirm/alert
    setPayModal({ userId, username, amount, affiliateCode });
    setPayProofFile(null);
    setPayProofPreview('');
    setPayProofNote('');
  };

  const processPayoutRequest = async (requestId, status) => {
    const label = status === 'paid' ? 'marquer comme PAYÉE' : 'REJETER';
    if (!window.confirm(`Voulez-vous ${label} cette demande de paiement ?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/affiliate-payout-requests/${requestId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        alert(data.detail || 'Erreur lors du traitement de la demande.');
        return;
      }
      // Refresh both the requests list AND the affiliates list (paid_commission updated)
      await fetchData();
    } catch (e) {
      console.error('Error processing payout:', e);
      alert('Erreur réseau.');
    }
  };

  const updateSuggestionStatus = async (id, status) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/suggestions/${id}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      if (res.ok) await fetchData();
    } catch (e) { console.error('Error updating suggestion:', e); }
  };

  const deleteSuggestion = async (id) => {
    if (!window.confirm('Supprimer définitivement cette suggestion ?')) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/suggestions/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) await fetchData();
    } catch (e) { console.error('Error deleting suggestion:', e); }
  };

  const handleProofFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setPayProofFile(null);
      setPayProofPreview('');
      return;
    }
    if (!['image/png', 'image/jpeg', 'image/jpg', 'image/webp'].includes(file.type)) {
      alert('Format non supporté (PNG, JPG ou WEBP)');
      e.target.value = '';
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      alert('Image trop lourde (max 4 Mo)');
      e.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setPayProofFile(file);
      setPayProofPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const submitPayAffiliate = async () => {
    if (!payModal) return;
    const { userId, username, amount, affiliateCode } = payModal;
    setPaySubmitting(true);
    setAffiliateLoading(prev => ({ ...prev, [affiliateCode]: true }));
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId || 'none'}/pay-affiliate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          amount: amount,
          affiliate_code: affiliateCode,
          proof_image: payProofPreview || null,
          proof_note: payProofNote || null,
        })
      });

      if (response.ok) {
        const data = await response.json();
        alert(`✅ ${data.paid_amount.toFixed(2)}€ marqué comme payé pour ${username}${data.proof_id ? ' (preuve jointe)' : ''}`);
        setPayModal(null);
        setPayProofFile(null);
        setPayProofPreview('');
        setPayProofNote('');
        fetchData();
      } else {
        const error = await response.json();
        alert(error.detail || 'Erreur lors du paiement');
      }
    } catch (error) {
      console.error('Error paying affiliate:', error);
      alert('Erreur lors du paiement');
    } finally {
      setPaySubmitting(false);
      setAffiliateLoading(prev => ({ ...prev, [affiliateCode]: false }));
    }
  };

  const openAffiliateDetails = async (affiliateCode) => {
    if (!affiliateCode) return;
    setDetailsLoading(true);
    setDetailsModal({ loading: true, code: affiliateCode });
    try {
      const res = await fetch(`${API_URL}/api/admin/affiliates/${affiliateCode}/details`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDetailsModal(data);
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || "Impossible de charger les détails");
        setDetailsModal(null);
      }
    } catch (e) {
      console.error(e);
      alert("Erreur lors du chargement des détails");
      setDetailsModal(null);
    } finally {
      setDetailsLoading(false);
    }
  };

  const openAdminProof = async (proofId) => {
    if (!proofId) return;
    setDetailsProofLoadingId(proofId);
    try {
      const res = await fetch(`${API_URL}/api/payment-proof/${proofId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDetailsProof(data);
      } else {
        alert("Impossible de charger la preuve");
      }
    } catch (e) {
      console.error(e);
      alert("Erreur lors du chargement de la preuve");
    } finally {
      setDetailsProofLoadingId(null);
    }
  };

  const copyAffiliateLink = (code) => {
    const url = `${window.location.origin}/${code}`;
    navigator.clipboard.writeText(url);
    setDetailsCopied(true);
    setTimeout(() => setDetailsCopied(false), 2000);
  };

  const renameAffiliateLink = async () => {
    const newCode = (newLinkCode || '').trim();
    if (!newCode) return;
    setSavingLink(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/affiliates/${detailsModal.code}/rename`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_code: newCode })
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.detail || 'Erreur lors du renommage du lien');
        return;
      }
      setDetailsModal({ ...detailsModal, code: data.code });
      setEditingLink(false);
      setNewLinkCode('');
      fetchData();
    } catch (e) {
      alert('Erreur réseau lors du renommage');
    } finally {
      setSavingLink(false);
    }
  };

  // Helper Applications Functions
  const fetchHelperApplications = async () => {
    setHelperLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/helper-applications`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setHelperApplications(await res.json());
    } catch (e) { console.error(e); }
    finally { setHelperLoading(false); }
  };

  const updateHelperApplication = async (appId, status, note) => {
    try {
      const body = {};
      if (status !== undefined) body.status = status;
      if (note !== undefined) body.admin_note = note;
      const res = await fetch(`${API_URL}/api/admin/helper-applications/${appId}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (res.ok) fetchHelperApplications();
    } catch (e) { console.error(e); }
  };

  const sendCandidateMessage = async (appId) => {
    const msg = (helperMessages[appId] || '').trim();
    if (!msg) { alert('Écris un message avant de l\'envoyer.'); return; }
    setSendingCandidateMsg(appId);
    try {
      const res = await fetch(`${API_URL}/api/admin/helper-applications/${appId}/message`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: msg })
      });
      if (res.ok) {
        alert('✅ Message envoyé au candidat en MP Discord !');
        setHelperMessages(prev => ({ ...prev, [appId]: '' }));
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || 'Erreur lors de l\'envoi du message');
      }
    } catch (e) {
      console.error(e);
      alert('Erreur lors de l\'envoi du message');
    } finally {
      setSendingCandidateMsg(null);
    }
  };

  const deleteHelperApplication = async (appId) => {
    if (!window.confirm('Supprimer cette candidature ?')) return;
    try {
      await fetch(`${API_URL}/api/admin/helper-applications/${appId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchHelperApplications();
    } catch (e) { console.error(e); }
  };

  const fetchRecruitmentSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/recruitment-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setRecruitmentSettings(await res.json());
    } catch (e) { console.error(e); }
  };

  const updateRecruitmentSettings = async (next) => {
    setSavingRecruitmentSettings(true);
    setRecruitmentSettings(next); // optimistic
    try {
      const res = await fetch(`${API_URL}/api/admin/recruitment-settings`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ roles: next }),
      });
      if (res.ok) setRecruitmentSettings(await res.json());
    } catch (e) { console.error(e); }
    finally { setSavingRecruitmentSettings(false); }
  };

  const toggleRoleFlag = (role, flag) => {
    const next = {
      ...recruitmentSettings,
      [role]: { ...recruitmentSettings[role], [flag]: !recruitmentSettings[role][flag] },
    };
    updateRecruitmentSettings(next);
  };

  useEffect(() => {
    if (activeTab === 'recruitment' && isAdmin) {
      fetchHelperApplications();
      fetchRecruitmentSettings();
    }
  }, [activeTab, isAdmin]);

  // Support Tickets
  const fetchSupportTickets = async () => {
    setSupportLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/support/tickets`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setSupportTickets(await res.json());
    } catch (e) {}
    finally { setSupportLoading(false); }
  };

  const updateSupportTicket = async (ticketId, status, note) => {
    try {
      const body = { status };
      if (note !== undefined) body.admin_note = note;
      const res = await fetch(`${API_URL}/api/admin/support/tickets/${ticketId}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (res.ok) fetchSupportTickets();
    } catch (e) {}
  };

  useEffect(() => {
    if (activeTab === 'support' && isAdmin) fetchSupportTickets();
  }, [activeTab, isAdmin]);

  // ============ ADMINS MANAGEMENT ============
  const [adminsList, setAdminsList] = useState({ manual_admins: [], discord_admins: [], total: 0 });
  const [adminsLoading, setAdminsLoading] = useState(false);
  const [adminAddId, setAdminAddId] = useState('');
  const [adminAddNote, setAdminAddNote] = useState('');
  const [adminAddLookup, setAdminAddLookup] = useState(null);
  const [adminAddSubmitting, setAdminAddSubmitting] = useState(false);
  const [adminAddError, setAdminAddError] = useState('');

  const fetchAdmins = async () => {
    setAdminsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/admins`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAdminsList(data);
      }
    } catch (e) {
      console.error('Failed to load admins', e);
    } finally {
      setAdminsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'admins' && isAdmin) fetchAdmins();
  }, [activeTab, isAdmin]);

  // ============ CLIENT DISCOUNT (Promos Clients) ============
  const [clientDiscount, setClientDiscount] = useState({ enabled: false, rules: {} });
  const [clientDiscountSaving, setClientDiscountSaving] = useState(false);
  const [clientDiscountSaved, setClientDiscountSaved] = useState(false);
  const SERVICE_LABELS = {
    windows:          { label: 'Service Windows',          color: 'cyan',    basePrice: 19,    group: 'services' },
    pack:             { label: 'Pack Complet',             color: 'amber',   basePrice: 32,    group: 'services' },
    bios:             { label: 'Optimisation BIOS',        color: 'green',   basePrice: 13.99, group: 'services' },
    aqmoptitool:      { label: 'AqmOptiTool',              color: 'violet',  basePrice: 3,     group: 'services' },
    licence_1:        { label: 'Licence — 1 utilisation',  color: 'indigo',  basePrice: 14.99, group: 'licences' },
    licence_2:        { label: 'Licence — 2 utilisations', color: 'indigo',  basePrice: 27.99, group: 'licences' },
    licence_3:        { label: 'Licence — 3 utilisations', color: 'indigo',  basePrice: 39.99, group: 'licences' },
    licence_4:        { label: 'Licence — 4 utilisations', color: 'indigo',  basePrice: 49.99, group: 'licences' },
    licence_5:        { label: 'Licence — 5 utilisations', color: 'indigo',  basePrice: 59.99, group: 'licences' },
    licence_lifetime: { label: 'Licence — Lifetime',       color: 'amber',   basePrice: 79.99, group: 'licences' },
  };

  const fetchClientDiscount = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/client-discount`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setClientDiscount(data);
      }
    } catch (e) { console.error('client discount fetch failed', e); }
  };

  const saveClientDiscount = async () => {
    setClientDiscountSaving(true);
    setClientDiscountSaved(false);
    try {
      const payload = {
        ...clientDiscount,
        rules: Object.fromEntries(
          Object.entries(clientDiscount.rules || {}).map(([k, r]) => [
            k, { ...r, value: parseFloat(String(r.value).replace(',', '.')) || 0 }
          ])
        )
      };
      const res = await fetch(`${API_URL}/api/admin/client-discount`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        const data = await res.json();
        setClientDiscount(data);
        setClientDiscountSaved(true);
        setTimeout(() => setClientDiscountSaved(false), 2500);
      }
    } catch (e) { console.error('save discount failed', e); }
    finally { setClientDiscountSaving(false); }
  };

  useEffect(() => {
    if (activeTab === 'client-discount' && isAdmin) fetchClientDiscount();
  }, [activeTab, isAdmin]);

  // Live lookup when admin types a Discord ID
  useEffect(() => {
    const id = adminAddId.trim();
    if (!/^\d{15,25}$/.test(id)) { setAdminAddLookup(null); return; }
    let cancelled = false;
    const handle = setTimeout(async () => {
      try {
        const res = await fetch(`${API_URL}/api/admin/admins/lookup/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setAdminAddLookup(data);
      } catch (_) {}
    }, 350);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [adminAddId, token]);

  const addAdmin = async () => {
    const id = adminAddId.trim();
    if (!/^\d{15,25}$/.test(id)) {
      setAdminAddError('Discord ID invalide (chiffres uniquement).');
      return;
    }
    setAdminAddSubmitting(true);
    setAdminAddError('');
    try {
      const res = await fetch(`${API_URL}/api/admin/admins`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          discord_id: id,
          username: adminAddLookup?.username || null,
          note: adminAddNote.trim() || null
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setAdminAddError(data.detail || 'Erreur lors de l\'ajout.');
        return;
      }
      setAdminAddId('');
      setAdminAddNote('');
      setAdminAddLookup(null);
      await fetchAdmins();
    } catch (e) {
      setAdminAddError('Erreur réseau.');
    } finally {
      setAdminAddSubmitting(false);
    }
  };

  const removeAdmin = async (discordId, username) => {
    if (!window.confirm(`Retirer ${username || discordId} des admins ?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/admins/${discordId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        await fetchAdmins();
      } else {
        const data = await res.json().catch(() => ({}));
        alert(data.detail || 'Erreur lors de la suppression.');
      }
    } catch (_) {
      alert('Erreur réseau.');
    }
  };

  const getServiceName = (type) => {
    const names = {
      'windows': 'Service Windows',
      'app-tools-trial': 'App-Tools Trial',
      'app-tools-premium': 'App-Tools Premium',
      'app-tools-lifetime': 'App-Tools Lifetime',
      'bios': 'Service BIOS'
    };
    return names[type] || type;
  };

  const getStatusBadge = (status) => {
    const badges = {
      'pending': { bg: 'bg-yellow-500/20', text: 'text-yellow-400', icon: Clock, label: 'En attente' },
      'in_progress': { bg: 'bg-blue-500/20', text: 'text-blue-400', icon: AlertCircle, label: 'En cours' },
      'completed': { bg: 'bg-green-500/20', text: 'text-green-400', icon: CheckCircle, label: 'Terminé' },
      'cancelled': { bg: 'bg-cyan-500/20', text: 'text-cyan-400', icon: XCircle, label: 'Annulé' },
      'refunded': { bg: 'bg-red-500/20', text: 'text-red-300', icon: XCircle, label: 'Remboursé' }
    };
    const badge = badges[status] || badges['pending'];
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 ${badge.bg} ${badge.text} text-xs rounded-full`}>
        <Icon className="w-3 h-3" />
        {badge.label}
      </span>
    );
  };

  const getTypeBadge = (type) => {
    const types = {
      'info': { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Info' },
      'warning': { bg: 'bg-yellow-500/20', text: 'text-yellow-400', label: 'Attention' },
      'success': { bg: 'bg-green-500/20', text: 'text-green-400', label: 'Succès' },
      'promo': { bg: 'bg-purple-500/20', text: 'text-purple-400', label: 'Promo' }
    };
    const t = types[type] || types['info'];
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 ${t.bg} ${t.text} text-xs rounded-full`}>
        {t.label}
      </span>
    );
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = order.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         order.id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!isAdmin) return null;

  return (
    <div className="min-h-screen py-4 md:py-8 px-3 md:px-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 md:mb-8 gap-3">
          <div>
            <div className="flex items-center gap-2 md:gap-3 mb-1 md:mb-2">
              <Shield className="w-6 h-6 md:w-8 md:h-8 text-cyan-500" />
              <h1 className="text-xl md:text-3xl font-bold text-white">Panel Admin</h1>
            </div>
            <p className="text-gray-400 text-xs md:text-base">Gestion des commandes, utilisateurs et annonces</p>
          </div>
          <div className="text-left sm:text-right">
            <p className="text-white font-medium text-sm md:text-base">{user?.username}</p>
            <p className="text-[10px] md:text-xs text-cyan-400">Administrateur</p>
          </div>
        </div>

        {/* Maintenance banner — full width, always visible */}
        <div className="mb-6 md:mb-8">
          <MaintenanceToggle token={token} />
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4 mb-6 md:mb-8">
            <div className="bg-zinc-900/50 border border-cyan-900/30 rounded-xl p-3 md:p-4">
              <div className="flex items-center gap-2 md:gap-3">
                <div className="w-8 h-8 md:w-10 md:h-10 rounded-lg bg-blue-600/20 flex items-center justify-center flex-shrink-0">
                  <Users className="w-4 h-4 md:w-5 md:h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-xl md:text-2xl font-bold text-white">{stats.total_users}</p>
                  <p className="text-[10px] md:text-xs text-gray-400">Utilisateurs</p>
                </div>
              </div>
            </div>
            
            <div className="bg-zinc-900/50 border border-cyan-900/30 rounded-xl p-3 md:p-4">
              <div className="flex items-center gap-2 md:gap-3">
                <div className="w-8 h-8 md:w-10 md:h-10 rounded-lg bg-green-600/20 flex items-center justify-center flex-shrink-0">
                  <Package className="w-4 h-4 md:w-5 md:h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-xl md:text-2xl font-bold text-white">{stats.total_orders}</p>
                  <p className="text-[10px] md:text-xs text-gray-400">Commandes</p>
                </div>
              </div>
            </div>
            
            <div className="bg-zinc-900/50 border border-cyan-900/30 rounded-xl p-3 md:p-4">
              <div className="flex items-center gap-2 md:gap-3">
                <div className="w-8 h-8 md:w-10 md:h-10 rounded-lg bg-yellow-600/20 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-4 h-4 md:w-5 md:h-5 text-yellow-400" />
                </div>
                <div>
                  <p className="text-xl md:text-2xl font-bold text-white">{stats.pending_orders}</p>
                  <p className="text-[10px] md:text-xs text-gray-400">En attente</p>
                </div>
              </div>
            </div>
            
            <div className="bg-zinc-900/50 border border-cyan-900/30 rounded-xl p-3 md:p-4">
              <div className="flex items-center gap-2 md:gap-3">
                <div className="w-8 h-8 md:w-10 md:h-10 rounded-lg bg-purple-600/20 flex items-center justify-center flex-shrink-0">
                  <Bell className="w-4 h-4 md:w-5 md:h-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-xl md:text-2xl font-bold text-white">{announcements.filter(a => a.active).length}</p>
                  <p className="text-[10px] md:text-xs text-gray-400">Annonces</p>
                </div>
              </div>
            </div>
            
            {/* Active Users Card */}
            <div className="bg-zinc-900/50 border border-green-500/30 rounded-xl p-3 md:p-4 col-span-2 md:col-span-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 md:gap-3">
                  <div className="w-8 h-8 md:w-10 md:h-10 rounded-lg bg-green-600/20 flex items-center justify-center flex-shrink-0 relative">
                    <Wifi className="w-4 h-4 md:w-5 md:h-5 text-green-400" />
                    <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full animate-pulse"></span>
                  </div>
                  <div>
                    <p className="text-xl md:text-2xl font-bold text-green-400">{activeUsers.total_count || 0}</p>
                    <p className="text-[10px] md:text-xs text-gray-400">
                      {activeUsers.count || 0} connecté{(activeUsers.count || 0) > 1 ? 's' : ''} • {activeUsers.anonymous_count || 0} visiteur{(activeUsers.anonymous_count || 0) > 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setActiveTab('online')}
                  className="px-3 py-1.5 bg-green-600/20 hover:bg-green-600/30 border border-green-500/30 rounded-lg text-green-400 text-xs font-medium transition-colors"
                >
                  Voir détails
                </button>
              </div>
              {(activeUsers.users.length > 0 || activeUsers.anonymous?.length > 0) && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {activeUsers.users.slice(0, 5).map((u) => (
                    <div key={u.user_id} className="flex items-center gap-2 bg-zinc-800/50 rounded-full px-3 py-1">
                      {u.avatar ? (
                        <img src={`https://cdn.discordapp.com/avatars/${u.discord_id}/${u.avatar}.png`} alt="" className="w-5 h-5 rounded-full" />
                      ) : (
                        <div className="w-5 h-5 rounded-full bg-gray-600 flex items-center justify-center text-[10px] text-white">
                          {u.username[0].toUpperCase()}
                        </div>
                      )}
                      <span className="text-xs text-gray-300">{u.username}</span>
                      <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                    </div>
                  ))}
                  {activeUsers.anonymous?.slice(0, 3).map((v, i) => (
                    <div key={`anon-${i}`} className="flex items-center gap-2 bg-zinc-800/50 rounded-full px-3 py-1">
                      <div className="w-5 h-5 rounded-full bg-gray-700 flex items-center justify-center text-[10px] text-gray-400">
                        ?
                      </div>
                      <span className="text-xs text-gray-500 font-mono">{v.ip.substring(0, 12)}...</span>
                      <span className="w-2 h-2 bg-yellow-500 rounded-full"></span>
                    </div>
                  ))}
                  {(activeUsers.users.length + (activeUsers.anonymous?.length || 0)) > 8 && (
                    <div className="flex items-center gap-1 bg-zinc-800/50 rounded-full px-3 py-1 text-xs text-gray-400">
                      +{(activeUsers.users.length + (activeUsers.anonymous?.length || 0)) - 8} autres
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex flex-wrap gap-1.5 md:gap-2 mb-4 md:mb-6 pb-2">
          <button
            onClick={() => setActiveTab('orders')}
            className={`px-3 md:px-4 py-1.5 md:py-2 rounded-lg font-medium transition-all text-xs md:text-sm whitespace-nowrap ${
              activeTab === 'orders' 
                ? 'bg-cyan-600 text-white' 
                : 'bg-zinc-900 text-gray-400 hover:text-white'
            }`}
          >
            Commandes
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`px-3 md:px-4 py-1.5 md:py-2 rounded-lg font-medium transition-all text-xs md:text-sm whitespace-nowrap ${
              activeTab === 'users' 
                ? 'bg-cyan-600 text-white' 
                : 'bg-zinc-900 text-gray-400 hover:text-white'
            }`}
          >
            Utilisateurs
          </button>
          <button
            onClick={() => setActiveTab('announcements')}
            className={`px-3 md:px-4 py-1.5 md:py-2 rounded-lg font-medium transition-all flex items-center gap-1 md:gap-2 text-xs md:text-sm whitespace-nowrap ${
              activeTab === 'announcements' 
                ? 'bg-cyan-600 text-white' 
                : 'bg-zinc-900 text-gray-400 hover:text-white'
            }`}
          >
            <Megaphone className="w-3 h-3 md:w-4 md:h-4" />
            Annonces
          </button>
          <button
            onClick={() => setActiveTab('referrals')}
            className={`px-3 md:px-4 py-1.5 md:py-2 rounded-lg font-medium transition-all flex items-center gap-1 md:gap-2 text-xs md:text-sm whitespace-nowrap ${
              activeTab === 'referrals' 
                ? 'bg-cyan-600 text-white' 
                : 'bg-zinc-900 text-gray-400 hover:text-white'
            }`}
          >
            <Gift className="w-3 h-3 md:w-4 md:h-4" />
            Parrainage
          </button>
          <button
            onClick={() => setActiveTab('bans')}
            className={`px-3 md:px-4 py-1.5 md:py-2 rounded-lg font-medium transition-all flex items-center gap-1 md:gap-2 text-xs md:text-sm whitespace-nowrap ${
              activeTab === 'bans' 
                ? 'bg-red-600 text-white' 
                : 'bg-zinc-900 text-gray-400 hover:text-white'
            }`}
          >
            <Ban className="w-3 h-3 md:w-4 md:h-4" />
            Bannissements
          </button>
          <button
            onClick={() => setActiveTab('online')}
            className={`px-3 md:px-4 py-1.5 md:py-2 rounded-lg font-medium transition-all flex items-center gap-1 md:gap-2 text-xs md:text-sm whitespace-nowrap ${
              activeTab === 'online' 
                ? 'bg-green-600 text-white' 
                : 'bg-zinc-900 text-gray-400 hover:text-white'
            }`}
          >
            <Wifi className="w-3 h-3 md:w-4 md:h-4" />
            En ligne ({activeUsers.total_count || 0})
          </button>
          <button
            onClick={() => setActiveTab('promos')}
            className={`px-3 md:px-4 py-1.5 md:py-2 rounded-lg font-medium transition-all flex items-center gap-1 md:gap-2 text-xs md:text-sm whitespace-nowrap ${
              activeTab === 'promos' 
                ? 'bg-orange-600 text-white' 
                : 'bg-zinc-900 text-gray-400 hover:text-white'
            }`}
          >
            <Zap className="w-3 h-3 md:w-4 md:h-4" />
            Promos
          </button>
          <button
            onClick={() => setActiveTab('user-affiliates')}
            className={`relative px-3 md:px-4 py-1.5 md:py-2 rounded-lg font-medium transition-all flex items-center gap-1 md:gap-2 text-xs md:text-sm whitespace-nowrap ${
              activeTab === 'user-affiliates' 
                ? 'bg-purple-600 text-white' 
                : 'bg-zinc-900 text-gray-400 hover:text-white'
            }`}
          >
            <TrendingUp className="w-3 h-3 md:w-4 md:h-4" />
            Affiliés
            {payoutRequests.filter(r => r.status === 'pending').length > 0 && (
              <span
                data-testid="payout-pending-badge"
                className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-orange-500 text-white text-[10px] font-bold animate-pulse"
              >
                {payoutRequests.filter(r => r.status === 'pending').length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('recruitment')}
            data-testid="admin-tab-recruitment"
            className={`px-3 md:px-4 py-1.5 md:py-2 rounded-lg font-medium transition-all flex items-center gap-1 md:gap-2 text-xs md:text-sm whitespace-nowrap ${
              activeTab === 'recruitment' 
                ? 'bg-cyan-600 text-white' 
                : 'bg-zinc-900 text-gray-400 hover:text-white'
            }`}
          >
            <Briefcase className="w-3 h-3 md:w-4 md:h-4" />
            Recrutement
            {helperApplications.filter(a => a.status === 'pending').length > 0 && (
              <span className="ml-1 px-1.5 py-0.5 bg-yellow-500 text-black text-[10px] font-bold rounded-full">
                {helperApplications.filter(a => a.status === 'pending').length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('suggestions')}
            data-testid="admin-tab-suggestions"
            className={`relative px-3 md:px-4 py-1.5 md:py-2 rounded-lg font-medium transition-all flex items-center gap-1 md:gap-2 text-xs md:text-sm whitespace-nowrap ${
              activeTab === 'suggestions'
                ? 'bg-amber-600 text-white'
                : 'bg-zinc-900 text-gray-400 hover:text-white'
            }`}
          >
            <Lightbulb className="w-3 h-3 md:w-4 md:h-4" />
            Suggestions
            {suggestions.filter(s => s.status === 'new').length > 0 && (
              <span
                data-testid="suggestions-new-badge"
                className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-amber-400 text-black text-[10px] font-bold animate-pulse"
              >
                {suggestions.filter(s => s.status === 'new').length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('support')}
            data-testid="admin-tab-support"
            className={`px-3 md:px-4 py-1.5 md:py-2 rounded-lg font-medium transition-all flex items-center gap-1 md:gap-2 text-xs md:text-sm whitespace-nowrap ${
              activeTab === 'support' 
                ? 'bg-blue-600 text-white' 
                : 'bg-zinc-900 text-gray-400 hover:text-white'
            }`}
          >
            <MessageSquare className="w-3 h-3 md:w-4 md:h-4" />
            Support
            {supportTickets.filter(t => t.status === 'open').length > 0 && (
              <span className="ml-1 px-1.5 py-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full">
                {supportTickets.filter(t => t.status === 'open').length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('admins')}
            data-testid="admin-tab-admins"
            className={`px-3 md:px-4 py-1.5 md:py-2 rounded-lg font-medium transition-all flex items-center gap-1 md:gap-2 text-xs md:text-sm whitespace-nowrap ${
              activeTab === 'admins'
                ? 'bg-fuchsia-600 text-white'
                : 'bg-zinc-900 text-gray-400 hover:text-white'
            }`}
          >
            <UserCog className="w-3 h-3 md:w-4 md:h-4" />
            Admins
          </button>
          <button
            onClick={() => setActiveTab('client-discount')}
            data-testid="admin-tab-client-discount"
            className={`px-3 md:px-4 py-1.5 md:py-2 rounded-lg font-medium transition-all flex items-center gap-1 md:gap-2 text-xs md:text-sm whitespace-nowrap ${
              activeTab === 'client-discount'
                ? 'bg-green-600 text-white'
                : 'bg-zinc-900 text-gray-400 hover:text-white'
            }`}
          >
            <Crown className="w-3 h-3 md:w-4 md:h-4" />
            Promos Clients
          </button>
          <button
            onClick={() => setActiveTab('faq-bot')}
            data-testid="admin-tab-faq-bot"
            className={`px-3 md:px-4 py-1.5 md:py-2 rounded-lg font-medium transition-all flex items-center gap-1 md:gap-2 text-xs md:text-sm whitespace-nowrap ${
              activeTab === 'faq-bot'
                ? 'bg-amber-600 text-white'
                : 'bg-zinc-900 text-gray-400 hover:text-white'
            }`}
          >
            <BotIcon className="w-3 h-3 md:w-4 md:h-4" />
            FAQ Bot
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            data-testid="admin-tab-analytics"
            className={`px-3 md:px-4 py-1.5 md:py-2 rounded-lg font-medium transition-all flex items-center gap-1 md:gap-2 text-xs md:text-sm whitespace-nowrap ${
              activeTab === 'analytics'
                ? 'bg-purple-600 text-white'
                : 'bg-zinc-900 text-gray-400 hover:text-white'
            }`}
          >
            <BarChartIcon className="w-3 h-3 md:w-4 md:h-4" />
            Analytics
          </button>
          <button
            onClick={() => setActiveTab('chat-history')}
            data-testid="admin-tab-chat-history"
            className={`px-3 md:px-4 py-1.5 md:py-2 rounded-lg font-medium transition-all flex items-center gap-1 md:gap-2 text-xs md:text-sm whitespace-nowrap ${
              activeTab === 'chat-history'
                ? 'bg-indigo-600 text-white'
                : 'bg-zinc-900 text-gray-400 hover:text-white'
            }`}
          >
            <MessageSquare className="w-3 h-3 md:w-4 md:h-4" />
            Chats IA
          </button>
          <button
            onClick={() => setActiveTab('pricing')}
            data-testid="admin-tab-pricing"
            className={`px-3 md:px-4 py-1.5 md:py-2 rounded-lg font-medium transition-all flex items-center gap-1 md:gap-2 text-xs md:text-sm whitespace-nowrap ${
              activeTab === 'pricing'
                ? 'bg-green-600 text-white'
                : 'bg-zinc-900 text-gray-400 hover:text-white'
            }`}
          >
            <DollarSign className="w-3 h-3 md:w-4 md:h-4" />
            Prix
          </button>
          <button
            onClick={() => setActiveTab('aqmoptitool')}
            data-testid="admin-tab-aqmoptitool"
            className={`px-3 md:px-4 py-1.5 md:py-2 rounded-lg font-medium transition-all flex items-center gap-1 md:gap-2 text-xs md:text-sm whitespace-nowrap ${
              activeTab === 'aqmoptitool'
                ? 'bg-cyan-600 text-white'
                : 'bg-zinc-900 text-gray-400 hover:text-white'
            }`}
          >
            <Star className="w-3 h-3 md:w-4 md:h-4" />
            AqmOptiTool
          </button>
          <button
            onClick={() => setActiveTab('polls')}
            data-testid="admin-tab-polls"
            className={`px-3 md:px-4 py-1.5 md:py-2 rounded-lg font-medium transition-all flex items-center gap-1 md:gap-2 text-xs md:text-sm whitespace-nowrap ${
              activeTab === 'polls'
                ? 'bg-cyan-600 text-white'
                : 'bg-zinc-900 text-gray-400 hover:text-white'
            }`}
          >
            <BarChartIcon className="w-3 h-3 md:w-4 md:h-4" />
            Sondages
          </button>
          <button
            onClick={() => setActiveTab('site-stats')}
            data-testid="admin-tab-site-stats"
            className={`px-3 md:px-4 py-1.5 md:py-2 rounded-lg font-medium transition-all flex items-center gap-1 md:gap-2 text-xs md:text-sm whitespace-nowrap ${
              activeTab === 'site-stats'
                ? 'bg-cyan-600 text-white'
                : 'bg-zinc-900 text-gray-400 hover:text-white'
            }`}
          >
            <BarChartIcon className="w-3 h-3 md:w-4 md:h-4" />
            Chiffres site
          </button>
          <button
            onClick={() => setActiveTab('referral-test')}
            data-testid="admin-tab-referral-test"
            className={`px-3 md:px-4 py-1.5 md:py-2 rounded-lg font-medium transition-all flex items-center gap-1 md:gap-2 text-xs md:text-sm whitespace-nowrap ${
              activeTab === 'referral-test'
                ? 'bg-cyan-600 text-white'
                : 'bg-zinc-900 text-gray-400 hover:text-white'
            }`}
          >
            <GiftIcon className="w-3 h-3 md:w-4 md:h-4" />
            Parrainage
          </button>
        </div>

        {activeTab === 'faq-bot' && <FaqBotTab token={token} />}
        {activeTab === 'analytics' && <AnalyticsTab token={token} />}
        {activeTab === 'chat-history' && <ChatHistoryTab token={token} />}
        {activeTab === 'pricing' && <PricingTab token={token} />}
        {activeTab === 'aqmoptitool' && <AqmOptiToolTab token={token} />}
        {activeTab === 'polls' && <PollsTab token={token} />}
        {activeTab === 'site-stats' && <SiteStatsTab token={token} />}
        {activeTab === 'referral-test' && <ReferralTestTab token={token} />}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div className="space-y-4 md:space-y-6">

            {/* === Monthly Revenue Recap (CA mensuel) === */}
            <div data-testid="monthly-revenue-recap" className="bg-zinc-900/50 border border-emerald-900/30 rounded-xl overflow-hidden">
              <div className="p-4 md:p-5 border-b border-emerald-900/20 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <h3 className="text-white font-semibold text-base md:text-lg flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-sm">€</span>
                    Récap chiffre d'affaires
                  </h3>
                  <p className="text-gray-500 text-xs md:text-sm mt-0.5">
                    Totaux mensuels (paiements validés) — pour ta déclaration de CA
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-gray-400 text-xs md:text-sm">Année :</label>
                  <select
                    data-testid="revenue-year-select"
                    value={revenueYear}
                    onChange={(e) => setRevenueYear(parseInt(e.target.value))}
                    className="px-3 py-1.5 bg-zinc-800 border border-emerald-900/40 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-500"
                  >
                    {(revenueData?.available_years || [revenueYear]).map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Annual total banner */}
              <div className="px-4 md:px-5 py-3 md:py-4 bg-gradient-to-r from-emerald-950/40 via-emerald-900/20 to-transparent border-b border-emerald-900/20 flex items-center justify-between flex-wrap gap-2">
                <div>
                  <p className="text-gray-400 text-xs uppercase tracking-wider">Total {revenueYear}</p>
                  <p data-testid="revenue-year-total" className="text-emerald-300 text-2xl md:text-3xl font-bold tabular-nums">
                    {revenueLoading ? '...' : `${(revenueData?.total_revenue ?? 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`}
                  </p>
                  {(revenueData?.total_refunded ?? 0) > 0 && (
                    <p className="text-red-300/80 text-xs mt-0.5">
                      − {(revenueData.total_refunded).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} € remboursés (exclus du CA)
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-gray-400 text-xs uppercase tracking-wider">Commandes</p>
                  <p className="text-white text-2xl md:text-3xl font-bold tabular-nums">
                    {revenueLoading ? '...' : (revenueData?.total_count ?? 0)}
                  </p>
                </div>
              </div>

              {/* 12-month grid */}
              <div className="p-3 md:p-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2 md:gap-3">
                {(revenueData?.months || Array.from({ length: 12 }, (_, i) => ({ month: i + 1, count: 0, revenue: 0 }))).map((m) => {
                  const monthNames = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
                  const isCurrentMonth = m.month === (new Date().getMonth() + 1) && revenueYear === new Date().getFullYear();
                  const hasRevenue = (m.revenue || 0) > 0;
                  return (
                    <div
                      key={m.month}
                      data-testid={`revenue-month-${m.month}`}
                      className={`p-3 rounded-lg border transition-colors ${
                        isCurrentMonth
                          ? 'bg-emerald-500/10 border-emerald-500/40'
                          : hasRevenue
                            ? 'bg-zinc-800/60 border-emerald-900/30 hover:border-emerald-700/50'
                            : 'bg-zinc-800/30 border-zinc-700/30'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <p className={`text-xs font-medium uppercase tracking-wider ${isCurrentMonth ? 'text-emerald-300' : 'text-gray-400'}`}>
                          {monthNames[m.month - 1]}
                        </p>
                        {isCurrentMonth && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 font-semibold uppercase">En cours</span>
                        )}
                      </div>
                      <p className={`text-lg md:text-xl font-bold tabular-nums ${hasRevenue ? 'text-white' : 'text-gray-600'}`}>
                        {(m.revenue || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                      </p>
                      <p className="text-gray-500 text-[11px] mt-0.5">
                        {m.count} commande{m.count > 1 ? 's' : ''}
                        {m.cancelled_count > 0 && (
                          <span className="text-red-400/70"> · {m.cancelled_count} annulée{m.cancelled_count > 1 ? 's' : ''}</span>
                        )}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* === Daily Revenue Recap (CA par jour) === */}
            <div data-testid="daily-revenue-recap" className="bg-zinc-900/50 border border-cyan-900/30 rounded-xl overflow-hidden">
              <div className="p-4 md:p-5 border-b border-cyan-900/20 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <h3 className="text-white font-semibold text-base md:text-lg flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-cyan-500/15 border border-cyan-500/30 text-cyan-300 text-sm">📅</span>
                    CA par jour
                  </h3>
                  <p className="text-gray-500 text-xs md:text-sm mt-0.5">
                    Chiffre d'affaires jour par jour (paiements validés) — pour analyser chaque journée
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-gray-400 text-xs md:text-sm">Mois :</label>
                  <select
                    data-testid="daily-month-select"
                    value={dailyMonth}
                    onChange={(e) => setDailyMonth(parseInt(e.target.value))}
                    className="px-3 py-1.5 bg-zinc-800 border border-cyan-900/40 rounded-lg text-white text-sm focus:outline-none focus:border-cyan-500"
                  >
                    {['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'].map((mn, i) => (
                      <option key={i + 1} value={i + 1}>{mn}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Month total banner */}
              <div className="px-4 md:px-5 py-3 md:py-4 bg-gradient-to-r from-cyan-950/40 via-cyan-900/20 to-transparent border-b border-cyan-900/20 flex items-center justify-between flex-wrap gap-2">
                <div>
                  <p className="text-gray-400 text-xs uppercase tracking-wider">
                    Total {['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'][dailyMonth - 1]} {revenueYear}
                  </p>
                  <p data-testid="daily-month-total" className="text-cyan-300 text-2xl md:text-3xl font-bold tabular-nums">
                    {dailyLoading ? '...' : `${(dailyData?.total_revenue ?? 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-gray-400 text-xs uppercase tracking-wider">Commandes</p>
                  <p className="text-white text-2xl md:text-3xl font-bold tabular-nums">
                    {dailyLoading ? '...' : (dailyData?.total_count ?? 0)}
                  </p>
                </div>
              </div>

              {/* Days list — only days with activity shown prominently */}
              <div className="p-3 md:p-4 space-y-1.5">
                {(dailyData?.days || []).length === 0 && !dailyLoading && (
                  <p className="text-gray-500 text-sm text-center py-4">Aucune donnée pour ce mois</p>
                )}
                {(dailyData?.days || []).map((d) => {
                  const isToday = dailyData?.year === new Date().getFullYear()
                    && dailyData?.month === (new Date().getMonth() + 1)
                    && d.day === new Date().getDate();
                  const hasRevenue = (d.revenue || 0) > 0;
                  return (
                    <div
                      key={d.day}
                      data-testid={`daily-revenue-${d.day}`}
                      className={`flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg border transition-colors ${
                        isToday
                          ? 'bg-cyan-500/10 border-cyan-500/40'
                          : hasRevenue
                            ? 'bg-zinc-800/60 border-cyan-900/25'
                            : 'bg-zinc-800/20 border-zinc-700/20'
                      }`}
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div className={`flex flex-col items-center justify-center w-11 h-11 rounded-lg shrink-0 ${hasRevenue ? 'bg-cyan-500/15 text-cyan-200' : 'bg-zinc-700/30 text-gray-500'}`}>
                          <span className="text-[9px] uppercase leading-none font-semibold">{d.weekday.slice(0, 3)}</span>
                          <span className="text-base font-bold leading-tight tabular-nums">{d.day}</span>
                        </div>
                        <div className="min-w-0">
                          <p className={`text-sm font-medium truncate ${hasRevenue ? 'text-white' : 'text-gray-500'}`}>
                            {d.weekday} {d.date}
                            {isToday && <span className="ml-2 text-[9px] px-1.5 py-0.5 rounded bg-cyan-500/20 text-cyan-300 font-semibold uppercase align-middle">Aujourd'hui</span>}
                          </p>
                          <p className="text-gray-500 text-[11px]">
                            {d.count} commande{d.count > 1 ? 's' : ''}
                            {d.cancelled_count > 0 && <span className="text-red-400/70"> · {d.cancelled_count} annulée{d.cancelled_count > 1 ? 's' : ''}</span>}
                            {d.refunded_count > 0 && <span className="text-orange-400/70"> · {d.refunded_count} remb.</span>}
                          </p>
                        </div>
                      </div>
                      <p className={`text-base md:text-lg font-bold tabular-nums shrink-0 ${hasRevenue ? 'text-cyan-300' : 'text-gray-600'}`}>
                        {(d.revenue || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* === Orders list (filters + table) === */}
            <div className="bg-zinc-900/50 border border-cyan-900/30 rounded-xl overflow-hidden">
            {/* Filters */}
            <div className="p-3 md:p-4 border-b border-cyan-900/20 flex flex-col sm:flex-row gap-2 md:gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-zinc-800 border border-cyan-900/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 text-xs md:text-sm"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 md:px-4 py-2 bg-zinc-800 border border-cyan-900/30 rounded-lg text-white focus:outline-none focus:border-cyan-500 text-xs md:text-sm"
              >
                <option value="all">Tous</option>
                <option value="pending">En attente</option>
                <option value="in_progress">En cours</option>
                <option value="completed">Terminé</option>
                <option value="cancelled">Annulé</option>
                <option value="refunded">Remboursé</option>
              </select>
            </div>

            {/* Orders - Mobile Cards / Desktop Table */}
            <div className="md:hidden divide-y divide-cyan-900/20">
              {filteredOrders.map((order) => {
                const isRefunded = order.status === 'refunded';
                const displayPrice = order.price ?? order.total_price ?? order.amount ?? 0;
                return (
                <div key={order.id} className={`p-3 space-y-2 ${isRefunded ? 'opacity-60' : ''}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {order.user_avatar ? (
                        <img 
                          src={`https://cdn.discordapp.com/avatars/${order.discord_id}/${order.user_avatar}.png`}
                          alt="" 
                          className="w-8 h-8 rounded-full"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-cyan-600/30 flex items-center justify-center">
                          <Users className="w-4 h-4 text-cyan-400" />
                        </div>
                      )}
                      <div>
                        <p className="text-white font-medium text-sm">{order.username || 'Unknown'}</p>
                        <p className="text-gray-500 text-[10px]">#{order.id.slice(0, 8)}</p>
                      </div>
                    </div>
                    {getStatusBadge(order.status)}
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-400">{getServiceName(order.service_type)}</span>
                    <span className="text-gray-500">{new Date(order.created_at).toLocaleDateString('fr-FR')}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <button
                      onClick={() => updateOrderPrice(order.id, displayPrice)}
                      className={`text-sm font-bold tabular-nums ${isRefunded ? 'line-through text-red-300' : (displayPrice > 0 ? 'text-emerald-300' : 'text-amber-400')}`}
                    >
                      {(displayPrice || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                    </button>
                    {order.payment_method && (
                      <span className="text-gray-500 text-[10px] uppercase tracking-wider">{order.payment_method}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <select
                      value={order.status}
                      onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                      disabled={isRefunded}
                      className="flex-1 px-2 py-1.5 bg-zinc-800 border border-cyan-900/30 rounded text-[10px] text-white focus:outline-none disabled:opacity-50"
                    >
                      <option value="pending">En attente</option>
                      <option value="in_progress">En cours</option>
                      <option value="completed">Terminé</option>
                      <option value="cancelled">Annulé</option>
                      {isRefunded && <option value="refunded">Remboursé</option>}
                    </select>
                    <button
                      onClick={() => toggleOrderRefund(order.id, isRefunded)}
                      className={`px-2 py-1.5 rounded text-[10px] font-semibold ${
                        isRefunded
                          ? 'bg-amber-500/20 text-amber-300'
                          : 'bg-red-500/15 text-red-300'
                      }`}
                    >
                      {isRefunded ? '↩' : '€−'}
                    </button>
                    <button
                      onClick={() => deleteOrder(order.id)}
                      className="p-1.5 text-gray-400 hover:text-cyan-500 hover:bg-cyan-500/10 rounded transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                );
              })}
            </div>

            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-zinc-800/50">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs text-gray-400 font-medium">Client</th>
                    <th className="text-left px-4 py-3 text-xs text-gray-400 font-medium">Service</th>
                    <th className="text-left px-4 py-3 text-xs text-gray-400 font-medium">Montant</th>
                    <th className="text-left px-4 py-3 text-xs text-gray-400 font-medium">Date</th>
                    <th className="text-left px-4 py-3 text-xs text-gray-400 font-medium">Statut</th>
                    <th className="text-left px-4 py-3 text-xs text-gray-400 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-cyan-900/20">
                  {filteredOrders.map((order) => {
                    const isRefunded = order.status === 'refunded';
                    const displayPrice = order.price ?? order.total_price ?? order.amount ?? 0;
                    return (
                    <tr key={order.id} className={`hover:bg-cyan-950/10 transition-colors ${isRefunded ? 'opacity-60' : ''}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {order.user_avatar ? (
                            <img 
                              src={`https://cdn.discordapp.com/avatars/${order.discord_id}/${order.user_avatar}.png`}
                              alt="" 
                              className="w-8 h-8 rounded-full"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-cyan-600/30 flex items-center justify-center">
                              <Users className="w-4 h-4 text-cyan-400" />
                            </div>
                          )}
                          <div>
                            <p className="text-white font-medium text-sm">{order.username || 'Unknown'}</p>
                            <p className="text-gray-500 text-xs">#{order.id.slice(0, 8)}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-white text-sm">{getServiceName(order.service_type)}</span>
                        {order.payment_method && (
                          <p className="text-gray-500 text-[10px] uppercase tracking-wider mt-0.5">{order.payment_method}</p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => updateOrderPrice(order.id, displayPrice)}
                          data-testid={`order-price-${order.id}`}
                          title="Cliquer pour modifier le prix"
                          className={`text-sm font-semibold tabular-nums hover:underline ${isRefunded ? 'line-through text-red-300' : (displayPrice > 0 ? 'text-emerald-300' : 'text-amber-400')}`}
                        >
                          {(displayPrice || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-gray-400 text-sm">
                          {new Date(order.created_at).toLocaleDateString('fr-FR')}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {getStatusBadge(order.status)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <select
                            value={order.status}
                            onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                            disabled={isRefunded}
                            className="px-2 py-1 bg-zinc-800 border border-cyan-900/30 rounded text-xs text-white focus:outline-none disabled:opacity-50"
                          >
                            <option value="pending">En attente</option>
                            <option value="in_progress">En cours</option>
                            <option value="completed">Terminé</option>
                            <option value="cancelled">Annulé</option>
                            {isRefunded && <option value="refunded">Remboursé</option>}
                          </select>
                          <button
                            onClick={() => toggleOrderRefund(order.id, isRefunded)}
                            data-testid={`order-refund-${order.id}`}
                            title={isRefunded ? 'Annuler le remboursement' : 'Marquer comme remboursé (retire du CA)'}
                            className={`px-2 py-1 rounded text-[11px] font-semibold transition-colors ${
                              isRefunded
                                ? 'bg-amber-500/20 text-amber-300 hover:bg-amber-500/30'
                                : 'bg-red-500/15 text-red-300 hover:bg-red-500/25'
                            }`}
                          >
                            {isRefunded ? '↩ Annuler' : 'Rembourser'}
                          </button>
                          <button
                            onClick={() => deleteOrder(order.id)}
                            className="p-1.5 text-gray-400 hover:text-cyan-500 hover:bg-cyan-500/10 rounded transition-colors"
                            title="Supprimer"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
              
            {filteredOrders.length === 0 && (
              <div className="text-center py-8 md:py-12">
                <Package className="w-10 h-10 md:w-12 md:h-12 text-gray-600 mx-auto mb-2 md:mb-3" />
                <p className="text-gray-400 text-sm">Aucune commande trouvée</p>
              </div>
            )}
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="bg-zinc-900/50 border border-cyan-900/30 rounded-xl overflow-hidden">
            {/* Stats Header */}
            <div className="grid grid-cols-3 gap-2 md:gap-4 p-3 md:p-4 border-b border-cyan-900/20 bg-gradient-to-r from-cyan-950/20 to-transparent">
              <div className="text-center" data-testid="users-count-total">
                <div className="flex items-center justify-center gap-1.5 mb-1">
                  <Users className="w-3.5 h-3.5 md:w-4 md:h-4 text-cyan-400" />
                  <p className="text-[10px] md:text-xs text-gray-400 uppercase tracking-wide">Total</p>
                </div>
                <p className="text-xl md:text-2xl font-bold text-white">{users.length}</p>
              </div>
              <div className="text-center border-x border-cyan-900/20" data-testid="users-count-admins">
                <div className="flex items-center justify-center gap-1.5 mb-1">
                  <Shield className="w-3.5 h-3.5 md:w-4 md:h-4 text-cyan-400" />
                  <p className="text-[10px] md:text-xs text-gray-400 uppercase tracking-wide">Admins</p>
                </div>
                <p className="text-xl md:text-2xl font-bold text-cyan-400">{users.filter(u => u.is_admin).length}</p>
              </div>
              <div className="text-center" data-testid="users-count-banned">
                <div className="flex items-center justify-center gap-1.5 mb-1">
                  <Ban className="w-3.5 h-3.5 md:w-4 md:h-4 text-red-400" />
                  <p className="text-[10px] md:text-xs text-gray-400 uppercase tracking-wide">Bannis</p>
                </div>
                <p className="text-xl md:text-2xl font-bold text-red-400">{users.filter(u => u.last_ip && isUserBanned(u.last_ip)).length}</p>
              </div>
            </div>

            {/* Search Bar */}
            <div className="p-3 md:p-4 border-b border-cyan-900/20">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input
                  type="text"
                  placeholder="Rechercher par nom, email, IP ou Discord ID..."
                  value={userSearchTerm}
                  onChange={(e) => setUserSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-zinc-800/50 border border-cyan-900/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500/50 text-sm"
                />
                {userSearchTerm && (
                  <button
                    onClick={() => setUserSearchTerm('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                  >
                    <XCircle className="w-4 h-4" />
                  </button>
                )}
              </div>
              {userSearchTerm && (
                <p className="text-xs text-gray-500 mt-2">
                  {users.filter(u => 
                    u.username?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
                    u.email?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
                    u.discord_id?.includes(userSearchTerm) ||
                    u.last_ip?.includes(userSearchTerm)
                  ).length} résultat(s) trouvé(s)
                </p>
              )}
            </div>
            
            {/* Mobile Cards */}
            <div className="md:hidden divide-y divide-cyan-900/20">
              {users
                .filter(u => 
                  !userSearchTerm ||
                  u.username?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
                  u.email?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
                  u.discord_id?.includes(userSearchTerm) ||
                  u.last_ip?.includes(userSearchTerm)
                )
                .map((u) => (
                <div key={u.id} className="p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {u.avatar ? (
                        <img 
                          src={`https://cdn.discordapp.com/avatars/${u.discord_id}/${u.avatar}.png`}
                          alt="" 
                          className="w-8 h-8 rounded-full"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-cyan-600/30 flex items-center justify-center">
                          <Users className="w-4 h-4 text-cyan-400" />
                        </div>
                      )}
                      <div>
                        <span className="text-white font-medium text-sm">{u.username}</span>
                        {u.last_ip && (
                          <p className="text-gray-500 text-[9px] font-mono">{u.last_ip}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {u.is_admin ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-cyan-500/20 text-cyan-400 text-[10px] rounded-full">
                          <Shield className="w-2.5 h-2.5" />
                          Admin
                        </span>
                      ) : isUserBanned(u.last_ip) ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-500/20 text-red-400 text-[10px] rounded-full">
                          <Ban className="w-2.5 h-2.5" />
                          Banni
                        </span>
                      ) : u.last_ip && (
                        <Button
                          onClick={() => banUser(u.id, u.username)}
                          size="sm"
                          className="bg-red-600/20 hover:bg-red-600 text-red-400 hover:text-white text-[10px] px-2 py-0.5"
                        >
                          <Ban className="w-2.5 h-2.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-[10px] text-gray-500">
                    <span>ID: {u.discord_id}</span>
                    <span>{u.email || 'Pas d\'email'}</span>
                    <span>{u.created_at ? new Date(u.created_at).toLocaleDateString('fr-FR') : '-'}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-zinc-800/50">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs text-gray-400 font-medium">Utilisateur</th>
                    <th className="text-left px-4 py-3 text-xs text-gray-400 font-medium">Email</th>
                    <th className="text-left px-4 py-3 text-xs text-gray-400 font-medium">Discord ID</th>
                    <th className="text-left px-4 py-3 text-xs text-gray-400 font-medium">IP</th>
                    <th className="text-left px-4 py-3 text-xs text-gray-400 font-medium">Dernière connexion</th>
                    <th className="text-left px-4 py-3 text-xs text-gray-400 font-medium">Rôle</th>
                    <th className="text-right px-4 py-3 text-xs text-gray-400 font-medium">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-cyan-900/20">
                  {users
                    .filter(u => 
                      !userSearchTerm ||
                      u.username?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
                      u.email?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
                      u.discord_id?.includes(userSearchTerm) ||
                      u.last_ip?.includes(userSearchTerm)
                    )
                    .map((u) => (
                    <tr key={u.id} className={`hover:bg-cyan-950/10 transition-colors ${isUserBanned(u.last_ip) ? 'bg-red-950/20' : ''}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {u.avatar ? (
                            <img 
                              src={`https://cdn.discordapp.com/avatars/${u.discord_id}/${u.avatar}.png`}
                              alt="" 
                              className="w-8 h-8 rounded-full"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-cyan-600/30 flex items-center justify-center">
                              <Users className="w-4 h-4 text-cyan-400" />
                            </div>
                          )}
                          <span className="text-white font-medium">{u.username}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {u.email ? (
                          <span className="text-cyan-400 text-sm">{u.email}</span>
                        ) : (
                          <span className="text-gray-600 text-sm">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-sm">{u.discord_id}</td>
                      <td className="px-4 py-3">
                        {u.last_ip ? (
                          <span className="text-cyan-400 font-mono text-sm">{u.last_ip}</span>
                        ) : (
                          <span className="text-gray-600 text-sm">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-sm">
                        {u.last_login ? new Date(u.last_login).toLocaleString('fr-FR') : (u.created_at ? new Date(u.created_at).toLocaleDateString('fr-FR') : '-')}
                      </td>
                      <td className="px-4 py-3">
                        {u.is_admin ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-cyan-500/20 text-cyan-400 text-xs rounded-full">
                            <Shield className="w-3 h-3" />
                            Admin
                          </span>
                        ) : isUserBanned(u.discord_id) ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded-full">
                            <Ban className="w-3 h-3" />
                            Banni
                          </span>
                        ) : (
                          <span className="text-gray-500 text-xs">Utilisateur</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {!u.is_admin && !isUserBanned(u.discord_id) && (
                          <Button
                            onClick={() => banUser(u.id, u.username)}
                            size="sm"
                            className="bg-red-600/20 hover:bg-red-600 text-red-400 hover:text-white"
                            data-testid={`ban-user-${u.id}`}
                          >
                            <Ban className="w-3 h-3 mr-1" />
                            Bannir
                          </Button>
                        )}
                        {isUserBanned(u.discord_id) && (
                          <Button
                            onClick={() => unbanUser(u.discord_id, u.username)}
                            size="sm"
                            className="bg-green-600/20 hover:bg-green-600 text-green-400 hover:text-white"
                            data-testid={`unban-user-${u.id}`}
                          >
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Débannir
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
              
            {users.length === 0 && (
              <div className="text-center py-8 md:py-12">
                <Users className="w-10 h-10 md:w-12 md:h-12 text-gray-600 mx-auto mb-2 md:mb-3" />
                <p className="text-gray-400 text-sm">Aucun utilisateur trouvé</p>
              </div>
            )}
          </div>
        )}

        {/* Announcements Tab */}
        {activeTab === 'announcements' && (
          <div className="space-y-4 md:space-y-6">
            {/* New Announcement Button */}
            <div className="flex justify-end">
              <Button
                onClick={() => setShowNewAnnouncement(true)}
                className="bg-cyan-600 hover:bg-cyan-700 text-white flex items-center gap-1.5 md:gap-2 text-xs md:text-sm"
              >
                <Plus className="w-3 h-3 md:w-4 md:h-4" />
                Nouvelle annonce
              </Button>
            </div>

            {/* New Announcement Form */}
            {showNewAnnouncement && (
              <div className="bg-zinc-900/50 border border-cyan-500/50 rounded-xl p-4 md:p-6">
                <h3 className="text-white font-semibold mb-3 md:mb-4 flex items-center gap-2 text-sm md:text-base">
                  <Megaphone className="w-4 h-4 md:w-5 md:h-5 text-cyan-500" />
                  Créer une annonce
                </h3>
                
                <div className="space-y-3 md:space-y-4">
                  <div>
                    <label className="block text-xs md:text-sm text-gray-400 mb-1">Type</label>
                    <select
                      value={newAnnouncement.type}
                      onChange={(e) => setNewAnnouncement({...newAnnouncement, type: e.target.value})}
                      className="w-full px-3 md:px-4 py-2 bg-zinc-800 border border-cyan-900/30 rounded-lg text-white focus:outline-none focus:border-cyan-500 text-xs md:text-sm"
                    >
                      <option value="info">ℹ️ Information</option>
                      <option value="warning">⚠️ Attention</option>
                      <option value="success">✅ Succès</option>
                      <option value="promo">🏷️ Promotion</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-xs md:text-sm text-gray-400 mb-1">Titre</label>
                    <input
                      type="text"
                      value={newAnnouncement.title}
                      onChange={(e) => setNewAnnouncement({...newAnnouncement, title: e.target.value})}
                      placeholder="Ex: Nouveau service disponible !"
                      className="w-full px-3 md:px-4 py-2 bg-zinc-800 border border-cyan-900/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 text-xs md:text-sm"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-xs md:text-sm text-gray-400 mb-1">Contenu</label>
                    <textarea
                      value={newAnnouncement.content}
                      onChange={(e) => setNewAnnouncement({...newAnnouncement, content: e.target.value})}
                      placeholder="Décrivez votre annonce..."
                      rows={3}
                      className="w-full px-3 md:px-4 py-2 bg-zinc-800 border border-cyan-900/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 resize-none text-xs md:text-sm"
                    />
                  </div>
                  
                  <div className="flex gap-2 md:gap-3 justify-end">
                    <Button
                      onClick={() => setShowNewAnnouncement(false)}
                      variant="outline"
                      className="border-gray-600 text-gray-400 hover:bg-zinc-800 text-xs md:text-sm"
                    >
                      Annuler
                    </Button>
                    <Button
                      onClick={createAnnouncement}
                      disabled={announcementLoading || !newAnnouncement.title || !newAnnouncement.content}
                      className="bg-cyan-600 hover:bg-cyan-700 text-white disabled:opacity-50 text-xs md:text-sm"
                    >
                      {announcementLoading ? 'Création...' : 'Publier'}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Announcements List */}
            <div className="bg-zinc-900/50 border border-cyan-900/30 rounded-xl overflow-hidden">
              <div className="p-3 md:p-4 border-b border-cyan-900/20">
                <h3 className="text-white font-semibold text-sm md:text-base">Annonces existantes</h3>
              </div>
              
              {announcements.length === 0 ? (
                <div className="text-center py-8 md:py-12">
                  <Megaphone className="w-10 h-10 md:w-12 md:h-12 text-gray-600 mx-auto mb-2 md:mb-3" />
                  <p className="text-gray-400 text-sm">Aucune annonce créée</p>
                </div>
              ) : (
                <div className="divide-y divide-cyan-900/20">
                  {announcements.map((announcement) => (
                    <div key={announcement.id} className={`p-3 md:p-4 ${!announcement.active ? 'opacity-50' : ''}`}>
                      <div className="flex items-start justify-between gap-2 md:gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-1.5 md:gap-2 mb-1">
                            {getTypeBadge(announcement.type)}
                            <h4 className="text-white font-medium text-xs md:text-base truncate">{announcement.title}</h4>
                            {!announcement.active && (
                              <span className="text-[10px] md:text-xs text-gray-500">(Off)</span>
                            )}
                          </div>
                          <p className="text-gray-400 text-[10px] md:text-sm line-clamp-2">{announcement.content}</p>
                          <p className="text-gray-500 text-[10px] md:text-xs mt-1.5 md:mt-2">
                            {announcement.created_by} • {new Date(announcement.created_at).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                        
                        <div className="flex items-center gap-1 md:gap-2 flex-shrink-0">
                          <button
                            onClick={() => toggleAnnouncement(announcement.id)}
                            className={`p-1.5 md:p-2 rounded-lg transition-colors ${
                              announcement.active 
                                ? 'text-green-400 hover:bg-green-500/10' 
                                : 'text-gray-400 hover:bg-gray-500/10'
                            }`}
                            title={announcement.active ? 'Désactiver' : 'Activer'}
                          >
                            {announcement.active ? <Eye className="w-3 h-3 md:w-4 md:h-4" /> : <EyeOff className="w-3 h-3 md:w-4 md:h-4" />}
                          </button>
                          <button
                            onClick={() => deleteAnnouncement(announcement.id)}
                            className="p-1.5 md:p-2 text-gray-400 hover:text-cyan-500 hover:bg-cyan-500/10 rounded-lg transition-colors"
                            title="Supprimer"
                          >
                            <Trash2 className="w-3 h-3 md:w-4 md:h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Commands Tab */}
        {activeTab === 'commands' && (
          <div className="bg-zinc-900/50 border border-cyan-900/30 rounded-xl overflow-hidden">
            <div className="p-4 border-b border-cyan-900/20 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Zap className="w-5 h-5 text-yellow-400" />
                <div>
                  <h3 className="text-white font-medium">Commandes Personnalisées</h3>
                  <p className="text-xs text-gray-400">Créez des commandes Discord personnalisées</p>
                </div>
              </div>
              <Button
                onClick={() => setShowNewCommand(true)}
                className="bg-green-600 hover:bg-green-700 text-white text-xs md:text-sm"
              >
                <Plus className="w-4 h-4 mr-1" />
                Créer
              </Button>
            </div>

            {/* New Command Modal */}
            {showNewCommand && (
              <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
                <div className="bg-zinc-900 border border-cyan-900/30 rounded-xl w-full max-w-md">
                  <div className="p-4 border-b border-cyan-900/20 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Terminal className="w-5 h-5 text-cyan-400" />
                      <h3 className="text-white font-medium">Créer une commande</h3>
                    </div>
                    <button onClick={() => setShowNewCommand(false)} className="text-gray-400 hover:text-white">
                      <XCircle className="w-5 h-5" />
                    </button>
                  </div>
                  
                  <div className="p-4 space-y-4">
                    <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                      <p className="text-yellow-400 text-xs flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        Ce formulaire créera une commande Discord *nom
                      </p>
                    </div>
                    
                    <div>
                      <label className="text-gray-300 text-sm mb-1 block">Nom de la commande (sans *) *</label>
                      <input
                        type="text"
                        value={newCommand.name}
                        onChange={(e) => setNewCommand({...newCommand, name: e.target.value.toLowerCase().replace(/\s/g, '')})}
                        placeholder="info"
                        className="w-full px-3 py-2 bg-zinc-800 border border-cyan-900/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 text-sm"
                      />
                    </div>
                    
                    <div>
                      <label className="text-gray-300 text-sm mb-1 block">Description de la commande</label>
                      <input
                        type="text"
                        value={newCommand.description}
                        onChange={(e) => setNewCommand({...newCommand, description: e.target.value})}
                        placeholder="Affiche les informations du serveur"
                        className="w-full px-3 py-2 bg-zinc-800 border border-cyan-900/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 text-sm"
                      />
                    </div>
                    
                    <div>
                      <label className="text-gray-300 text-sm mb-1 block">Titre de l'embed *</label>
                      <input
                        type="text"
                        value={newCommand.embed_title}
                        onChange={(e) => setNewCommand({...newCommand, embed_title: e.target.value})}
                        placeholder="📋 Informations"
                        className="w-full px-3 py-2 bg-zinc-800 border border-cyan-900/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 text-sm"
                      />
                    </div>
                    
                    <div>
                      <label className="text-gray-300 text-sm mb-1 block">Contenu de l'embed *</label>
                      <textarea
                        value={newCommand.embed_content}
                        onChange={(e) => setNewCommand({...newCommand, embed_content: e.target.value})}
                        placeholder="Voici les informations...&#10;&#10;Utilise {user} pour mentionner l'utilisateur"
                        rows={4}
                        className="w-full px-3 py-2 bg-zinc-800 border border-cyan-900/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 text-sm resize-none"
                      />
                    </div>
                    
                    <div>
                      <label className="text-gray-300 text-sm mb-1 block">Couleur (hex sans #)</label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={newCommand.embed_color}
                          onChange={(e) => setNewCommand({...newCommand, embed_color: e.target.value.replace('#', '')})}
                          placeholder="3498DB"
                          maxLength={6}
                          className="flex-1 px-3 py-2 bg-zinc-800 border border-cyan-900/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 text-sm"
                        />
                        <div 
                          className="w-10 h-10 rounded-lg border border-cyan-900/30"
                          style={{ backgroundColor: `#${newCommand.embed_color || '3498DB'}` }}
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-4 border-t border-cyan-900/20 flex gap-2">
                    <Button
                      onClick={() => setShowNewCommand(false)}
                      variant="outline"
                      className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-800"
                    >
                      Annuler
                    </Button>
                    <Button
                      onClick={createCommand}
                      disabled={commandLoading || !newCommand.name || !newCommand.embed_title || !newCommand.embed_content}
                      className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white"
                    >
                      {commandLoading ? 'Création...' : 'Envoyer'}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Commands List */}
            <div className="p-4">
              {customCommands.length === 0 ? (
                <div className="text-center py-8">
                  <Terminal className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-400">Aucune commande personnalisée</p>
                  <p className="text-gray-500 text-sm">Cliquez sur "Créer" pour ajouter une commande</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {customCommands.map((cmd) => (
                    <div key={cmd.name} className="bg-zinc-800/50 border border-cyan-900/20 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <code className="text-cyan-400 font-mono text-sm">*{cmd.name}</code>
                            {cmd.description && (
                              <span className="text-gray-500 text-xs">- {cmd.description}</span>
                            )}
                          </div>
                          <div 
                            className="mt-2 p-3 rounded-lg border-l-4"
                            style={{ 
                              borderColor: `#${cmd.embed_color || '3498DB'}`,
                              backgroundColor: `#${cmd.embed_color || '3498DB'}10`
                            }}
                          >
                            <p className="text-white font-medium text-sm">{cmd.embed_title}</p>
                            <p className="text-gray-300 text-xs mt-1 whitespace-pre-wrap">{cmd.embed_content.substring(0, 100)}{cmd.embed_content.length > 100 ? '...' : ''}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => deleteCommand(cmd.name)}
                          className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors ml-2"
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}


        {/* Referrals Tab */}
        {activeTab === 'referrals' && (
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
                <div className="flex items-center justify-between mb-2">
                  <Gift className="w-8 h-8 text-cyan-400" />
                  <span className="text-3xl font-bold text-cyan-400">
                    {referralStats.reduce((sum, u) => sum + u.referral_points, 0)}
                  </span>
                </div>
                <p className="text-gray-400 text-sm">Points totaux distribués</p>
              </div>

              <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
                <div className="flex items-center justify-between mb-2">
                  <Users className="w-8 h-8 text-cyan-400" />
                  <span className="text-3xl font-bold text-cyan-400">
                    {referralStats.filter(u => u.referrals_count > 0).length}
                  </span>
                </div>
                <p className="text-gray-400 text-sm">Parrains actifs</p>
              </div>

              <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
                <div className="flex items-center justify-between mb-2">
                  <TrendingUp className="w-8 h-8 text-cyan-400" />
                  <span className="text-3xl font-bold text-cyan-400">
                    {referralStats.reduce((sum, u) => sum + u.referrals_count, 0)}
                  </span>
                </div>
                <p className="text-gray-400 text-sm">Total parrainages</p>
              </div>
            </div>

            {/* Users Referral Table */}
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
              <h3 className="text-white font-bold text-lg mb-4">Statistiques de Parrainage</h3>
              
              {referralStats.length === 0 ? (
                <p className="text-gray-400 text-center py-8">Aucune donnée de parrainage</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-zinc-800">
                        <th className="text-left text-gray-400 text-xs font-medium p-3">Utilisateur</th>
                        <th className="text-left text-gray-400 text-xs font-medium p-3">Code</th>
                        <th className="text-center text-gray-400 text-xs font-medium p-3">Points</th>
                        <th className="text-center text-gray-400 text-xs font-medium p-3">Parrainages</th>
                        <th className="text-left text-gray-400 text-xs font-medium p-3">Parrainés</th>
                        <th className="text-center text-gray-400 text-xs font-medium p-3">Récompenses</th>
                      </tr>
                    </thead>
                    <tbody>
                      {referralStats.map((user, index) => {
                        const canClaimWindows = user.referral_points >= 50;
                        const canClaimPack = user.referral_points >= 100;
                        
                        return (
                          <tr key={index} className="border-b border-zinc-800/50 hover:bg-zinc-800/30 transition-colors">
                            <td className="p-3">
                              <div className="flex items-center gap-3">
                                {user.avatar ? (
                                  <img 
                                    src={`https://cdn.discordapp.com/avatars/${user.discord_id}/${user.avatar}.png`}
                                    alt={user.username}
                                    className="w-8 h-8 rounded-full"
                                  />
                                ) : (
                                  <div className="w-8 h-8 bg-cyan-600/20 rounded-full flex items-center justify-center">
                                    <Users className="w-4 h-4 text-cyan-400" />
                                  </div>
                                )}
                                <div>
                                  <p className="text-white text-sm font-medium">{user.username}</p>
                                  {user.referred_by && (
                                    <p className="text-gray-500 text-xs">Parrainé par: {user.referred_by}</p>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="p-3">
                              <span className="text-cyan-400 font-mono text-sm font-semibold">
                                {user.referral_code}
                              </span>
                            </td>
                            <td className="p-3 text-center">
                              <div className="flex items-center justify-center gap-2">
                                <div className="inline-flex items-center gap-1 px-2 py-1 bg-cyan-950/30 border border-cyan-800/30 rounded-lg">
                                  <span className="text-cyan-400 font-bold">{user.referral_points}</span>
                                  <span className="text-gray-500 text-xs">pts</span>
                                </div>
                                {user.referral_points > 0 && (
                                  <button
                                    onClick={async () => {
                                      if (window.confirm(`Reset les points de ${user.username} à 0 ?`)) {
                                        try {
                                          const res = await fetch(`${API_URL}/api/admin/referrals/${user.user_id}/reset-points`, {
                                            method: 'POST',
                                            headers: { 'Authorization': `Bearer ${token}` }
                                          });
                                          if (res.ok) {
                                            setReferralStats(prev => prev.map(u => 
                                              u.user_id === user.user_id ? {...u, referral_points: 0} : u
                                            ));
                                          }
                                        } catch (err) {
                                          console.error('Error resetting points:', err);
                                        }
                                      }
                                    }}
                                    className="p-1 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                                    title="Reset les points"
                                  >
                                    <RotateCcw className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            </td>
                            <td className="p-3 text-center">
                              <span className="text-white font-medium">{user.referrals_count}</span>
                            </td>
                            <td className="p-3">
                              {user.referred_users.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {user.referred_users.slice(0, 3).map((name, i) => (
                                    <span key={i} className="text-xs bg-zinc-800 text-gray-300 px-2 py-1 rounded">
                                      {name}
                                    </span>
                                  ))}
                                  {user.referred_users.length > 3 && (
                                    <span className="text-xs text-gray-500">
                                      +{user.referred_users.length - 3}
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <span className="text-gray-500 text-sm">-</span>
                              )}
                            </td>
                            <td className="p-3">
                              <div className="flex items-center justify-center gap-2">
                                {canClaimWindows && (
                                  <div className="group relative">
                                    <div className="px-2 py-1 bg-cyan-600/20 border border-cyan-600/40 rounded text-xs text-cyan-400 font-semibold">
                                      Windows
                                    </div>
                                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-zinc-900 border border-cyan-600 rounded text-xs text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                                      Peut réclamer Windows (50pts)
                                    </div>
                                  </div>
                                )}
                                {canClaimPack && (
                                  <div className="group relative">
                                    <div className="px-2 py-1 bg-orange-600/20 border border-orange-600/40 rounded text-xs text-orange-400 font-semibold">
                                      Pack
                                    </div>
                                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-zinc-900 border border-orange-600 rounded text-xs text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                                      Peut réclamer Pack (100pts)
                                    </div>
                                  </div>
                                )}
                                {!canClaimWindows && !canClaimPack && (
                                  <span className="text-gray-500 text-sm">-</span>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Info Box */}
            <div className="bg-cyan-950/20 border border-cyan-800/30 rounded-xl p-4">
              <h4 className="text-cyan-400 font-semibold mb-2 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                Comment ça marche ?
              </h4>
              <ul className="text-gray-400 text-sm space-y-2">
                <li>• Les points sont attribués quand un parrainé <strong className="text-white">commande un service</strong></li>
                <li>• <strong className="text-cyan-400">Windows:</strong> 10 pts (Standard) / 12 pts (Prioritaire)</li>
                <li>• <strong className="text-orange-400">Pack Complet:</strong> 15 pts (Standard) / 18 pts (Prioritaire)</li>
                <li>• <strong className="text-cyan-400">50 points</strong> = 1 Service Windows gratuit</li>
                <li>• <strong className="text-orange-400">100 points</strong> = 1 Pack Complet gratuit</li>
                <li>• Les utilisateurs réclament leurs récompenses sur la page <strong className="text-white">Parrainage</strong></li>
                <li>• Un <strong className="text-white">ticket Discord</strong> est créé automatiquement lors de la réclamation</li>
              </ul>
            </div>
          </div>
        )}

        {/* Partners/Affiliates Tab */}
        {/* IP Bans Tab */}
        {activeTab === 'bans' && (
          <div className="space-y-4 md:space-y-6">
            {/* Current Visitor IP */}
            <div className="bg-zinc-900/50 border border-cyan-900/30 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-white font-semibold text-sm md:text-base">IP du visiteur actuel</h3>
                  <p className="text-gray-400 text-xs">Votre adresse IP actuelle</p>
                </div>
                <div className="px-4 py-2 bg-cyan-600/20 border border-cyan-500/30 rounded-lg">
                  <span className="text-cyan-400 font-mono text-sm md:text-base">{visitorIP || 'Chargement...'}</span>
                </div>
              </div>
            </div>

            {/* Site-banned Users List (by Discord ID) */}
            <div className="bg-zinc-900/50 border border-red-900/30 rounded-xl overflow-hidden">
              <div className="p-4 border-b border-red-900/20">
                <h3 className="text-white font-semibold flex items-center gap-2">
                  <Ban className="w-5 h-5 text-red-400" />
                  Utilisateurs bannis du site ({bannedUsers.length})
                </h3>
                <p className="text-gray-400 text-xs mt-1">
                  Blocage lié au compte Discord — empêche connexion, achats et tickets, peu importe l'IP.
                </p>
              </div>
              {bannedUsers.length === 0 ? (
                <div className="p-8 text-center">
                  <Shield className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-400">Aucun utilisateur banni du site</p>
                </div>
              ) : (
                <div className="divide-y divide-red-900/10">
                  {bannedUsers.map((b) => (
                    <div key={b.discord_id} className="flex items-center justify-between gap-3 p-4" data-testid={`banned-user-row-${b.discord_id}`}>
                      <div className="flex items-center gap-3 min-w-0">
                        {b.avatar ? (
                          <img
                            src={`https://cdn.discordapp.com/avatars/${b.discord_id}/${b.avatar}.png`}
                            alt={b.username}
                            className="w-9 h-9 rounded-full flex-shrink-0"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <div className="w-9 h-9 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0">
                            <Ban className="w-4 h-4 text-red-400" />
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="text-white text-sm font-medium truncate">{b.username || 'Inconnu'}</p>
                          <p className="text-gray-500 text-xs truncate">
                            Discord ID: {b.discord_id} • {b.reason || 'Non spécifié'}
                          </p>
                        </div>
                      </div>
                      <Button
                        onClick={() => unbanUser(b.discord_id, b.username)}
                        size="sm"
                        className="bg-green-600/20 hover:bg-green-600 text-green-400 hover:text-white flex-shrink-0"
                        data-testid={`unban-user-btn-${b.discord_id}`}
                      >
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Débannir
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Ban New IP Form */}
            <div className="bg-zinc-900/50 border border-red-900/30 rounded-xl p-4">
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2 text-sm md:text-base">
                <Ban className="w-5 h-5 text-red-400" />
                Bannir une nouvelle IP
              </h3>
              <div className="flex flex-col md:flex-row gap-3">
                <input
                  type="text"
                  placeholder="Adresse IP (ex: 192.168.1.1)"
                  value={newBanIP}
                  onChange={(e) => setNewBanIP(e.target.value)}
                  className="flex-1 px-4 py-2 bg-zinc-800 border border-red-900/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-red-500 text-sm"
                  data-testid="ban-ip-input"
                />
                <input
                  type="text"
                  placeholder="Raison (optionnel)"
                  value={newBanReason}
                  onChange={(e) => setNewBanReason(e.target.value)}
                  className="flex-1 px-4 py-2 bg-zinc-800 border border-red-900/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-red-500 text-sm"
                  data-testid="ban-reason-input"
                />
                <Button
                  onClick={banIP}
                  disabled={banLoading || !newBanIP.trim()}
                  className="bg-red-600 hover:bg-red-700 text-white px-6"
                  data-testid="ban-ip-btn"
                >
                  {banLoading ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Bannissement...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Ban className="w-4 h-4" />
                      Bannir
                    </span>
                  )}
                </Button>
              </div>
            </div>

            {/* Banned Users List */}
            <div className="bg-zinc-900/50 border border-cyan-900/30 rounded-xl overflow-hidden">
              <div className="p-4 border-b border-cyan-900/20">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <h3 className="text-white font-semibold flex items-center gap-2">
                    <Shield className="w-5 h-5 text-red-400" />
                    Utilisateurs bannis ({bannedIPs.length})
                  </h3>
                  {/* Search Bar */}
                  <div className="relative flex-1 md:max-w-xs">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <input
                      type="text"
                      placeholder="Rechercher IP ou raison..."
                      value={banSearchTerm}
                      onChange={(e) => setBanSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 bg-zinc-800/50 border border-cyan-900/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-red-500/50 text-sm"
                    />
                    {banSearchTerm && (
                      <button
                        onClick={() => setBanSearchTerm('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                      >
                        <XCircle className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
                {banSearchTerm && (
                  <p className="text-xs text-gray-500 mt-2">
                    {bannedIPs.filter(b => 
                      b.ip?.toLowerCase().includes(banSearchTerm.toLowerCase()) ||
                      b.reason?.toLowerCase().includes(banSearchTerm.toLowerCase())
                    ).length} résultat(s) trouvé(s)
                  </p>
                )}
              </div>
              
              {bannedIPs.length === 0 ? (
                <div className="p-8 text-center">
                  <Shield className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-400">Aucun utilisateur banni</p>
                </div>
              ) : bannedIPs.filter(b => 
                  !banSearchTerm ||
                  b.ip?.toLowerCase().includes(banSearchTerm.toLowerCase()) ||
                  b.reason?.toLowerCase().includes(banSearchTerm.toLowerCase())
                ).length === 0 ? (
                <div className="p-8 text-center">
                  <Search className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-400">Aucun résultat pour "{banSearchTerm}"</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-zinc-800/50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Utilisateur</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">IP</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Raison</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Date</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Banni par</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-400 uppercase">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-cyan-900/20">
                      {bannedIPs
                        .filter(b => 
                          !banSearchTerm ||
                          b.ip?.toLowerCase().includes(banSearchTerm.toLowerCase()) ||
                          b.reason?.toLowerCase().includes(banSearchTerm.toLowerCase()) ||
                          b.username?.toLowerCase().includes(banSearchTerm.toLowerCase())
                        )
                        .map((ban, index) => (
                        <tr key={index} className="hover:bg-zinc-800/30 transition-colors bg-red-950/10">
                          <td className="px-4 py-3">
                            {ban.username ? (
                              <div className="flex items-center gap-3">
                                {ban.avatar ? (
                                  <img 
                                    src={`https://cdn.discordapp.com/avatars/${ban.discord_id}/${ban.avatar}.png`}
                                    alt="" 
                                    className="w-8 h-8 rounded-full"
                                  />
                                ) : (
                                  <div className="w-8 h-8 rounded-full bg-red-600/30 flex items-center justify-center">
                                    <Ban className="w-4 h-4 text-red-400" />
                                  </div>
                                )}
                                <div>
                                  <span className="text-white font-medium">{ban.username}</span>
                                  {ban.discord_id && (
                                    <p className="text-gray-500 text-xs">ID: {ban.discord_id}</p>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <span className="text-gray-500 text-sm">IP inconnue</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-red-400 font-mono text-sm">{ban.ip}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-gray-300 text-sm">{ban.reason}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-gray-400 text-sm">
                              {ban.banned_at ? new Date(ban.banned_at).toLocaleString('fr-FR') : '-'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-gray-400 text-sm">{ban.banned_by}</span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <Button
                              onClick={() => unbanIP(ban.ip)}
                              variant="outline"
                              size="sm"
                              className="text-green-400 border-green-600/30 hover:bg-green-600/20 hover:text-green-300"
                              data-testid={`unban-${ban.ip}`}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Débannir
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Info Box */}
            <div className="bg-zinc-900/50 border border-yellow-900/30 rounded-xl p-4">
              <h4 className="text-yellow-400 font-semibold mb-2 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                Informations
              </h4>
              <ul className="text-gray-400 text-sm space-y-2">
                <li>• Les IPs bannies reçoivent une erreur <strong className="text-red-400">403 Accès interdit</strong></li>
                <li>• Le bannissement est immédiat et bloque toutes les requêtes</li>
                <li>• L'IP est enregistrée à chaque connexion de l'utilisateur</li>
                <li>• Vous pouvez bannir directement depuis l'onglet <strong className="text-cyan-400">Utilisateurs</strong></li>
              </ul>
            </div>
          </div>
        )}

        {/* Online Users Tab */}
        {activeTab === 'online' && (
          <div className="space-y-4">
            {/* Header */}
            <div className="bg-zinc-900/50 border border-green-500/30 rounded-xl p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-green-600/20 flex items-center justify-center relative">
                    <Wifi className="w-6 h-6 text-green-400" />
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full animate-pulse"></span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">Visiteurs en temps réel</h3>
                    <p className="text-gray-400 text-sm">Mis à jour automatiquement toutes les 10 secondes</p>
                  </div>
                </div>
                <div className="text-right flex gap-6">
                  <div>
                    <p className="text-2xl font-bold text-green-400">{activeUsers.count || 0}</p>
                    <p className="text-xs text-gray-500">Connectés</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-yellow-400">{activeUsers.anonymous_count || 0}</p>
                    <p className="text-xs text-gray-500">Visiteurs</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-cyan-400">{activeUsers.total_count || 0}</p>
                    <p className="text-xs text-gray-500">Total</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Connected Users */}
            <div className="bg-zinc-900/50 border border-green-500/30 rounded-xl overflow-hidden">
              <div className="p-3 border-b border-green-500/20 bg-green-900/20">
                <h4 className="text-green-400 font-semibold flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Utilisateurs connectés ({activeUsers.count || 0})
                </h4>
              </div>
              {activeUsers.users?.length > 0 ? (
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-cyan-900/20 bg-zinc-900/50">
                      <th className="p-3 text-left text-xs font-semibold text-gray-400">Utilisateur</th>
                      <th className="p-3 text-left text-xs font-semibold text-gray-400">Page actuelle</th>
                      <th className="p-3 text-center text-xs font-semibold text-gray-400">Activité</th>
                      <th className="p-3 text-center text-xs font-semibold text-gray-400">Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeUsers.users.map((u) => (
                      <tr key={u.user_id} className="border-b border-cyan-900/10 hover:bg-cyan-900/10 transition-colors">
                        <td className="p-3">
                          <div className="flex items-center gap-3">
                            {u.avatar ? (
                              <img 
                                src={`https://cdn.discordapp.com/avatars/${u.discord_id}/${u.avatar}.png`} 
                                alt="" 
                                className="w-10 h-10 rounded-full border-2 border-green-500/50" 
                              />
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-600 to-gray-800 flex items-center justify-center text-white font-bold border-2 border-green-500/50">
                                {u.username[0].toUpperCase()}
                              </div>
                            )}
                            <div>
                              <p className="text-white font-medium">{u.username}</p>
                              <p className="text-xs text-gray-500">ID: {u.discord_id}</p>
                            </div>
                          </div>
                        </td>
                        <td className="p-3">
                          <span className="px-2 py-1 bg-cyan-600/20 border border-cyan-600/30 rounded text-cyan-400 text-xs font-mono">
                            {u.page || '/'}
                          </span>
                        </td>
                        <td className="p-3 text-center">
                          <span className="text-gray-400 text-sm">Il y a {u.seconds_ago}s</span>
                        </td>
                        <td className="p-3 text-center">
                          <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-green-600/20 border border-green-500/30 rounded-full text-green-400 text-xs">
                            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                            Connecté
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="p-6 text-center text-gray-500">
                  Aucun utilisateur connecté
                </div>
              )}
            </div>

            {/* Anonymous Visitors */}
            <div className="bg-zinc-900/50 border border-yellow-500/30 rounded-xl overflow-hidden">
              <div className="p-3 border-b border-yellow-500/20 bg-yellow-900/20">
                <h4 className="text-yellow-400 font-semibold flex items-center gap-2">
                  <Eye className="w-4 h-4" />
                  Visiteurs anonymes ({activeUsers.anonymous_count || 0})
                </h4>
              </div>
              {activeUsers.anonymous?.length > 0 ? (
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-cyan-900/20 bg-zinc-900/50">
                      <th className="p-3 text-left text-xs font-semibold text-gray-400">Adresse IP</th>
                      <th className="p-3 text-left text-xs font-semibold text-gray-400">Page actuelle</th>
                      <th className="p-3 text-center text-xs font-semibold text-gray-400">Activité</th>
                      <th className="p-3 text-center text-xs font-semibold text-gray-400">Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeUsers.anonymous.map((v, i) => (
                      <tr key={`anon-${i}`} className="border-b border-cyan-900/10 hover:bg-cyan-900/10 transition-colors">
                        <td className="p-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-yellow-700 to-yellow-900 flex items-center justify-center text-yellow-300 font-bold border-2 border-yellow-500/50">
                              ?
                            </div>
                            <div>
                              <p className="text-white font-mono text-sm">{v.ip}</p>
                              <p className="text-xs text-gray-500">Visiteur anonyme</p>
                            </div>
                          </div>
                        </td>
                        <td className="p-3">
                          <span className="px-2 py-1 bg-cyan-600/20 border border-cyan-600/30 rounded text-cyan-400 text-xs font-mono">
                            {v.page || '/'}
                          </span>
                        </td>
                        <td className="p-3 text-center">
                          <span className="text-gray-400 text-sm">Il y a {v.seconds_ago}s</span>
                        </td>
                        <td className="p-3 text-center">
                          <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-yellow-600/20 border border-yellow-500/30 rounded-full text-yellow-400 text-xs">
                            <span className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></span>
                            Visiteur
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="p-6 text-center text-gray-500">
                  Aucun visiteur anonyme
                </div>
              )}
            </div>

            {/* Info Box */}
            <div className="bg-cyan-950/20 border border-cyan-800/30 rounded-xl p-4">
              <h4 className="text-cyan-400 font-semibold mb-2 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                Comment ça marche ?
              </h4>
              <ul className="text-gray-400 text-sm space-y-2">
                <li>• <strong className="text-green-400">Connectés</strong> : Utilisateurs authentifiés avec Discord</li>
                <li>• <strong className="text-yellow-400">Visiteurs</strong> : Personnes naviguant sans être connectées (identifiés par IP)</li>
                <li>• Un visiteur est considéré <strong className="text-white">en ligne</strong> s'il a été actif dans les 60 dernières secondes</li>
                <li>• Les données se rafraîchissent automatiquement toutes les <strong className="text-cyan-400">10 secondes</strong></li>
              </ul>
            </div>
          </div>
        )}

        {/* Promos Tab */}
        {activeTab === 'promos' && (
          <div className="space-y-6">
            {/* Flash Sale Section */}
            <div className="bg-gradient-to-r from-orange-900/30 to-red-900/30 border border-orange-600/30 rounded-xl p-4 md:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg md:text-xl font-bold text-white flex items-center gap-2">
                  <Zap className="w-5 h-5 text-orange-400" />
                  Vente Flash
                </h3>
                {flashSale ? (
                  <button
                    onClick={endFlashSale}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    Terminer la vente
                  </button>
                ) : (
                  <button
                    onClick={() => setShowNewFlashSale(true)}
                    className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg text-sm font-medium flex items-center gap-2 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Lancer une vente flash
                  </button>
                )}
              </div>

              {flashSale ? (
                <div className="bg-black/30 rounded-lg p-4">
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="flex-1">
                      <p className="text-white font-semibold text-lg">{flashSale.title}</p>
                      <p className="text-orange-400 text-2xl font-bold">-{flashSale.discount_percent}%</p>
                      {flashSale.live && (
                        <p className="text-pink-400 text-sm font-semibold flex items-center gap-1.5 mt-1" data-testid="admin-flash-live-status">
                          <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />🔴 EN DIRECT TikTok
                          {flashSale.tiktok_url && <a href={flashSale.tiktok_url} target="_blank" rel="noreferrer" className="underline hover:text-pink-300">(voir le lien)</a>}
                        </p>
                      )}
                      <p className="text-gray-400 text-sm mt-1">
                        Sur : {flashSale.services && flashSale.services.length > 0 
                          ? flashSale.services.map(s => ({windows:'Windows', pack:'Pack Complet', bios:'BIOS', licence:'Licences'}[s] || s)).join(', ')
                          : 'Tous les services'}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-gray-400 text-sm">Se termine le</p>
                      <p className="text-white font-mono">
                        {new Date(flashSale.end_time).toLocaleString('fr-FR')}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-gray-400 text-center py-4">
                  Aucune vente flash active. Lancez-en une pour afficher un banner sur le site !
                </p>
              )}

              {/* New Flash Sale Form */}
              {showNewFlashSale && (
                <form onSubmit={createFlashSale} className="mt-4 bg-black/30 rounded-lg p-4 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-gray-400 text-sm mb-1">Titre</label>
                      <input
                        type="text"
                        value={newFlashSale.title}
                        onChange={(e) => setNewFlashSale({ ...newFlashSale, title: e.target.value })}
                        className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-gray-400 text-sm mb-1">Réduction (%)</label>
                      <input
                        type="number"
                        value={newFlashSale.discount_percent}
                        onChange={(e) => setNewFlashSale({ ...newFlashSale, discount_percent: e.target.value })}
                        className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                        min="1"
                        max="90"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-gray-400 text-sm mb-1">Durée (heures)</label>
                      <input
                        type="number"
                        value={newFlashSale.duration_hours}
                        onChange={(e) => setNewFlashSale({ ...newFlashSale, duration_hours: e.target.value })}
                        className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                        min="1"
                        max="168"
                        required
                      />
                    </div>
                  </div>
                  
                  {/* Message personnalisé (gros titre affiché dans la bande) */}
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Message personnalisé (affiché en gros dans la bande)</label>
                    <input
                      type="text"
                      value={newFlashSale.banner_text}
                      onChange={(e) => setNewFlashSale({ ...newFlashSale, banner_text: e.target.value })}
                      placeholder="🎮 Joueurs Fortnite, profitez de -20% avant les FNCS !"
                      className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                      data-testid="flash-banner-text"
                    />
                    <p className="text-gray-500 text-xs mt-1">Laisse vide pour un message automatique. Astuce durée : 24h = 1 jour · 72h = 3 jours · 168h = 7 jours.</p>
                  </div>

                  {/* Services Selection */}
                  <div>
                    <label className="block text-gray-400 text-sm mb-2">Services concernés</label>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => setNewFlashSale({ ...newFlashSale, services: null })}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          !newFlashSale.services || newFlashSale.services.length === 0
                            ? 'bg-orange-600 text-white'
                            : 'bg-zinc-700 text-gray-300 hover:bg-zinc-600'
                        }`}
                      >
                        Tous les services
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const services = newFlashSale.services || [];
                          if (services.includes('windows')) {
                            setNewFlashSale({ ...newFlashSale, services: services.filter(s => s !== 'windows') });
                          } else {
                            setNewFlashSale({ ...newFlashSale, services: [...services, 'windows'] });
                          }
                        }}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          newFlashSale.services?.includes('windows')
                            ? 'bg-cyan-600 text-white'
                            : 'bg-zinc-700 text-gray-300 hover:bg-zinc-600'
                        }`}
                      >
                        Windows
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const services = newFlashSale.services || [];
                          if (services.includes('pack')) {
                            setNewFlashSale({ ...newFlashSale, services: services.filter(s => s !== 'pack') });
                          } else {
                            setNewFlashSale({ ...newFlashSale, services: [...services, 'pack'] });
                          }
                        }}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          newFlashSale.services?.includes('pack')
                            ? 'bg-amber-600 text-white'
                            : 'bg-zinc-700 text-gray-300 hover:bg-zinc-600'
                        }`}
                      >
                        Pack Complet
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const services = newFlashSale.services || [];
                          if (services.includes('bios')) {
                            setNewFlashSale({ ...newFlashSale, services: services.filter(s => s !== 'bios') });
                          } else {
                            setNewFlashSale({ ...newFlashSale, services: [...services, 'bios'] });
                          }
                        }}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          newFlashSale.services?.includes('bios')
                            ? 'bg-green-600 text-white'
                            : 'bg-zinc-700 text-gray-300 hover:bg-zinc-600'
                        }`}
                      >
                        BIOS
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const services = newFlashSale.services || [];
                          if (services.includes('licence')) {
                            setNewFlashSale({ ...newFlashSale, services: services.filter(s => s !== 'licence') });
                          } else {
                            setNewFlashSale({ ...newFlashSale, services: [...services, 'licence'] });
                          }
                        }}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          newFlashSale.services?.includes('licence')
                            ? 'bg-indigo-600 text-white'
                            : 'bg-zinc-700 text-gray-300 hover:bg-zinc-600'
                        }`}
                      >
                        Licences
                      </button>
                    </div>
                    <p className="text-gray-500 text-xs mt-2">
                      {!newFlashSale.services || newFlashSale.services.length === 0 
                        ? 'La réduction s\'appliquera à tous les services' 
                        : `La réduction s'appliquera à : ${newFlashSale.services.join(', ')}`}
                    </p>
                  </div>

                  {/* Live TikTok */}
                  <div className="rounded-lg border border-pink-600/30 bg-pink-950/20 p-4">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newFlashSale.live}
                        onChange={(e) => setNewFlashSale({ ...newFlashSale, live: e.target.checked })}
                        className="w-4 h-4 accent-pink-500"
                        data-testid="flash-live-toggle"
                      />
                      <span className="text-white font-medium flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                        Je suis en live TikTok (affiche une bande « EN DIRECT » sur le site)
                      </span>
                    </label>
                    {newFlashSale.live && (
                      <div className="mt-3">
                        <label className="block text-gray-400 text-sm mb-1">Lien de ton live TikTok</label>
                        <input
                          type="url"
                          value={newFlashSale.tiktok_url}
                          onChange={(e) => setNewFlashSale({ ...newFlashSale, tiktok_url: e.target.value })}
                          placeholder="https://www.tiktok.com/@ton_compte/live"
                          className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                          data-testid="flash-tiktok-url"
                        />
                        <p className="text-gray-500 text-xs mt-1">Le bouton « Regarder le live TikTok » ouvrira ce lien.</p>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <button
                      type="submit"
                      disabled={flashSaleLoading}
                      className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg text-sm font-medium disabled:opacity-50"
                    >
                      {flashSaleLoading ? 'Création...' : 'Lancer la vente flash'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowNewFlashSale(false)}
                      className="px-4 py-2 bg-zinc-700 hover:bg-zinc-600 text-white rounded-lg text-sm"
                    >
                      Annuler
                    </button>
                  </div>
                </form>
              )}
            </div>

            {/* Promo Codes Section */}
            <div className="bg-zinc-900/50 border border-cyan-900/30 rounded-xl overflow-hidden">
              <div className="p-4 border-b border-cyan-900/20 flex items-center justify-between">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <Percent className="w-5 h-5 text-cyan-400" />
                  Codes Promo
                </h3>
                <button
                  onClick={() => setShowNewPromo(true)}
                  className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg text-sm font-medium flex items-center gap-2 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Nouveau code
                </button>
              </div>

              {/* New Promo Code Form */}
              {showNewPromo && (
                <form onSubmit={createPromoCode} className="p-4 bg-zinc-800/50 border-b border-cyan-900/20 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-gray-400 text-sm mb-1">Code</label>
                      <input
                        type="text"
                        value={newPromo.code}
                        onChange={(e) => setNewPromo({ ...newPromo, code: e.target.value.toUpperCase() })}
                        placeholder="EX: PROMO20"
                        className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white uppercase"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-gray-400 text-sm mb-1">Réduction (%)</label>
                      <input
                        type="number"
                        value={newPromo.discount_percent}
                        onChange={(e) => setNewPromo({ ...newPromo, discount_percent: e.target.value })}
                        className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                        min="1"
                        max="90"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-gray-400 text-sm mb-1">Utilisations max (vide = illimité)</label>
                      <input
                        type="number"
                        value={newPromo.max_uses}
                        onChange={(e) => setNewPromo({ ...newPromo, max_uses: e.target.value })}
                        placeholder="Illimité"
                        className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                        min="1"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-400 text-sm mb-1">Expire le (optionnel)</label>
                      <input
                        type="datetime-local"
                        value={newPromo.valid_until}
                        onChange={(e) => setNewPromo({ ...newPromo, valid_until: e.target.value })}
                        className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                      />
                    </div>
                  </div>
                  
                  {/* Services Selection */}
                  <div>
                    <label className="block text-gray-400 text-sm mb-2">Services concernés</label>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => setNewPromo({ ...newPromo, services: null })}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          !newPromo.services || newPromo.services.length === 0
                            ? 'bg-cyan-600 text-white'
                            : 'bg-zinc-700 text-gray-300 hover:bg-zinc-600'
                        }`}
                      >
                        Tous les services
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const services = newPromo.services || [];
                          if (services.includes('windows')) {
                            setNewPromo({ ...newPromo, services: services.filter(s => s !== 'windows') });
                          } else {
                            setNewPromo({ ...newPromo, services: [...services, 'windows'] });
                          }
                        }}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          newPromo.services?.includes('windows')
                            ? 'bg-cyan-600 text-white'
                            : 'bg-zinc-700 text-gray-300 hover:bg-zinc-600'
                        }`}
                      >
                        Windows
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const services = newPromo.services || [];
                          if (services.includes('pack')) {
                            setNewPromo({ ...newPromo, services: services.filter(s => s !== 'pack') });
                          } else {
                            setNewPromo({ ...newPromo, services: [...services, 'pack'] });
                          }
                        }}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          newPromo.services?.includes('pack')
                            ? 'bg-amber-600 text-white'
                            : 'bg-zinc-700 text-gray-300 hover:bg-zinc-600'
                        }`}
                      >
                        Pack Complet
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const services = newPromo.services || [];
                          if (services.includes('bios')) {
                            setNewPromo({ ...newPromo, services: services.filter(s => s !== 'bios') });
                          } else {
                            setNewPromo({ ...newPromo, services: [...services, 'bios'] });
                          }
                        }}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          newPromo.services?.includes('bios')
                            ? 'bg-green-600 text-white'
                            : 'bg-zinc-700 text-gray-300 hover:bg-zinc-600'
                        }`}
                      >
                        BIOS
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const services = newPromo.services || [];
                          if (services.includes('licence')) {
                            setNewPromo({ ...newPromo, services: services.filter(s => s !== 'licence') });
                          } else {
                            setNewPromo({ ...newPromo, services: [...services, 'licence'] });
                          }
                        }}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          newPromo.services?.includes('licence')
                            ? 'bg-indigo-600 text-white'
                            : 'bg-zinc-700 text-gray-300 hover:bg-zinc-600'
                        }`}
                      >
                        Licences
                      </button>
                    </div>
                    <p className="text-gray-500 text-xs mt-2">
                      {!newPromo.services || newPromo.services.length === 0 
                        ? 'Le code sera valide pour tous les services' 
                        : `Le code sera valide pour : ${newPromo.services.map(s => ({windows:'Windows', pack:'Pack Complet', bios:'BIOS', licence:'Licences'}[s] || s)).join(', ')}`}
                    </p>
                  </div>
                  
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      disabled={promoLoading}
                      className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg text-sm font-medium disabled:opacity-50"
                    >
                      {promoLoading ? 'Création...' : 'Créer le code'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowNewPromo(false)}
                      className="px-4 py-2 bg-zinc-700 hover:bg-zinc-600 text-white rounded-lg text-sm"
                    >
                      Annuler
                    </button>
                  </div>
                </form>
              )}

              {/* Promo Codes Table */}
              {promoCodes.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-zinc-800/50">
                      <tr>
                        <th className="text-left p-3 text-gray-400 font-medium text-sm">Code</th>
                        <th className="text-center p-3 text-gray-400 font-medium text-sm">Réduction</th>
                        <th className="text-center p-3 text-gray-400 font-medium text-sm">Services</th>
                        <th className="text-center p-3 text-gray-400 font-medium text-sm">Utilisations</th>
                        <th className="text-center p-3 text-gray-400 font-medium text-sm">Expire</th>
                        <th className="text-center p-3 text-gray-400 font-medium text-sm">Statut</th>
                        <th className="text-center p-3 text-gray-400 font-medium text-sm">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-800">
                      {promoCodes.map((promo) => {
                        const isExpired = promo.valid_until && new Date(promo.valid_until) < new Date();
                        const isExhausted = promo.max_uses && promo.current_uses >= promo.max_uses;
                        
                        return (
                          <tr key={promo.code} className="hover:bg-zinc-800/30">
                            <td className="p-3">
                              <code className="px-2 py-1 bg-cyan-600/20 border border-cyan-600/30 rounded text-cyan-400 font-mono">
                                {promo.code}
                              </code>
                            </td>
                            <td className="p-3 text-center">
                              <span className="text-orange-400 font-bold">-{promo.discount_percent}%</span>
                            </td>
                            <td className="p-3 text-center text-gray-400 text-xs">
                              {promo.services && promo.services.length > 0 
                                ? promo.services.map(s => ({windows:'Win', pack:'Pack', bios:'BIOS', licence:'Lic.'}[s] || s)).join(', ')
                                : 'Tous'}
                            </td>
                            <td className="p-3 text-center text-gray-300">
                              {promo.current_uses || 0} / {promo.max_uses || '∞'}
                            </td>
                            <td className="p-3 text-center text-gray-400 text-sm">
                              {promo.valid_until 
                                ? new Date(promo.valid_until).toLocaleDateString('fr-FR')
                                : 'Jamais'}
                            </td>
                            <td className="p-3 text-center">
                              {!promo.active ? (
                                <span className="px-2 py-1 bg-gray-600/30 border border-gray-500/30 rounded-full text-gray-400 text-xs">
                                  Désactivé
                                </span>
                              ) : isExpired ? (
                                <span className="px-2 py-1 bg-red-600/30 border border-red-500/30 rounded-full text-red-400 text-xs">
                                  Expiré
                                </span>
                              ) : isExhausted ? (
                                <span className="px-2 py-1 bg-yellow-600/30 border border-yellow-500/30 rounded-full text-yellow-400 text-xs">
                                  Épuisé
                                </span>
                              ) : (
                                <span className="px-2 py-1 bg-green-600/30 border border-green-500/30 rounded-full text-green-400 text-xs">
                                  Actif
                                </span>
                              )}
                            </td>
                            <td className="p-3 text-center">
                              <div className="flex items-center justify-center gap-2">
                                <button
                                  onClick={() => togglePromoCode(promo.code, promo.active)}
                                  className={`p-1.5 rounded-lg transition-colors ${
                                    promo.active 
                                      ? 'bg-yellow-600/20 text-yellow-400 hover:bg-yellow-600/30' 
                                      : 'bg-green-600/20 text-green-400 hover:bg-green-600/30'
                                  }`}
                                  title={promo.active ? 'Désactiver' : 'Activer'}
                                >
                                  {promo.active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                                <button
                                  onClick={() => deletePromoCode(promo.code)}
                                  className="p-1.5 bg-red-600/20 text-red-400 hover:bg-red-600/30 rounded-lg transition-colors"
                                  title="Supprimer"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-8 text-center text-gray-500">
                  <Percent className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Aucun code promo créé</p>
                  <p className="text-sm mt-1">Cliquez sur "Nouveau code" pour en créer un</p>
                </div>
              )}
            </div>

            {/* Info Box */}
            <div className="bg-cyan-950/20 border border-cyan-800/30 rounded-xl p-4">
              <h4 className="text-cyan-400 font-semibold mb-2 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                Comment ça marche ?
              </h4>
              <ul className="text-gray-400 text-sm space-y-2">
                <li>• <strong className="text-orange-400">Vente Flash</strong> : Affiche un banner en haut du site avec un countdown. La réduction s'applique automatiquement à toutes les commandes.</li>
                <li>• <strong className="text-cyan-400">Codes Promo</strong> : Les clients peuvent entrer un code lors du checkout pour bénéficier d'une réduction.</li>
                <li>• Si une vente flash est active ET qu'un code promo est utilisé, c'est la <strong className="text-white">plus grosse réduction</strong> qui s'applique.</li>
              </ul>
            </div>
          </div>
        )}

        {/* User Affiliates Tab */}
        {activeTab === 'user-affiliates' && (
          <div className="space-y-6">
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-950/40 to-pink-950/20 border border-purple-500/30 rounded-xl p-4 md:p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-purple-500/20 rounded-xl flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <h3 className="text-lg md:text-xl font-bold text-white">Gestion des Affiliés</h3>
                  <p className="text-gray-400 text-sm">Attribue le rôle affilié aux utilisateurs</p>
                </div>
              </div>
            </div>

            {/* Payout requests panel */}
            {(() => {
              const pendingReqs = payoutRequests.filter(r => r.status === 'pending');
              if (pendingReqs.length === 0) return null;
              return (
                <div data-testid="payout-requests-panel" className="bg-gradient-to-r from-orange-950/40 to-amber-950/20 border border-orange-500/40 rounded-xl overflow-hidden">
                  <div className="px-4 py-3 md:px-5 md:py-4 border-b border-orange-500/30 flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 md:w-10 md:h-10 rounded-lg bg-orange-500/20 border border-orange-500/40 flex items-center justify-center">
                        <DollarSign className="w-4 h-4 md:w-5 md:h-5 text-orange-300" />
                      </div>
                      <div>
                        <h4 className="text-white font-semibold text-sm md:text-base">Demandes de paiement à traiter</h4>
                        <p className="text-orange-300/80 text-[11px] md:text-xs">Les affiliés ont réclamé leur paiement PayPal</p>
                      </div>
                    </div>
                    <span className="px-2.5 py-1 rounded-full bg-orange-500 text-white text-xs font-bold tabular-nums">
                      {pendingReqs.length} en attente
                    </span>
                  </div>
                  <div className="divide-y divide-orange-500/15">
                    {pendingReqs.map(req => (
                      <div key={req.id} className="p-3 md:p-4 flex flex-col md:flex-row md:items-center gap-3 md:gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-white font-semibold text-sm md:text-base">{req.username || 'Utilisateur'}</span>
                            <span className="px-1.5 py-0.5 rounded bg-purple-500/15 border border-purple-500/30 text-purple-300 text-[10px] font-bold uppercase tracking-wider">
                              {req.affiliate_code}
                            </span>
                            <span className="text-gray-500 text-[11px]">
                              {new Date(req.requested_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                            <span className="inline-flex items-center gap-1 text-emerald-300 font-bold text-lg md:text-xl tabular-nums">
                              {Number(req.amount).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                            </span>
                            <span className="text-gray-400 text-xs md:text-sm">→</span>
                            <span className="text-white text-xs md:text-sm font-mono break-all">{req.paypal_email}</span>
                          </div>
                          {req.note && (
                            <p className="mt-1.5 text-gray-400 text-xs italic">"{req.note}"</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button
                            onClick={() => processPayoutRequest(req.id, 'rejected')}
                            data-testid={`payout-reject-${req.id}`}
                            className="px-3 py-2 rounded-lg bg-zinc-800 hover:bg-red-500/15 text-gray-400 hover:text-red-300 text-xs font-semibold border border-zinc-700 hover:border-red-500/40 transition-colors"
                          >
                            Rejeter
                          </button>
                          <button
                            onClick={() => processPayoutRequest(req.id, 'paid')}
                            data-testid={`payout-pay-${req.id}`}
                            className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs md:text-sm font-bold transition-colors shadow-[0_4px_15px_-4px_rgba(16,185,129,0.5)] inline-flex items-center gap-1.5"
                          >
                            <Check className="w-3.5 h-3.5" />
                            Marquer payé
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher un utilisateur..."
                value={affiliateSearchTerm}
                onChange={(e) => setAffiliateSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-zinc-900 border border-purple-900/30 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
              />
            </div>

            {/* Users Table */}
            <div className="bg-zinc-900/50 border border-purple-900/30 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-zinc-800/50">
                    <tr>
                      <th className="text-left p-4 text-gray-400 font-medium text-sm">Utilisateur</th>
                      <th className="text-center p-4 text-gray-400 font-medium text-sm">Code</th>
                      <th className="text-center p-4 text-gray-400 font-medium text-sm">%</th>
                      <th className="text-center p-4 text-gray-400 font-medium text-sm">Clics</th>
                      <th className="text-center p-4 text-gray-400 font-medium text-sm">Connexions</th>
                      <th className="text-center p-4 text-gray-400 font-medium text-sm">Ventes</th>
                      <th className="text-center p-4 text-gray-400 font-medium text-sm">Total</th>
                      <th className="text-center p-4 text-gray-400 font-medium text-sm">En attente</th>
                      <th className="text-center p-4 text-gray-400 font-medium text-sm">Payé</th>
                      <th className="text-right p-4 text-gray-400 font-medium text-sm">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-purple-900/20">
                    {userAffiliates
                      .filter(u => 
                        !affiliateSearchTerm ||
                        u.username?.toLowerCase().includes(affiliateSearchTerm.toLowerCase()) ||
                        u.affiliate_code?.toLowerCase().includes(affiliateSearchTerm.toLowerCase())
                      )
                      .map((u) => (
                        <tr key={u.id} className={`hover:bg-purple-950/10 transition-colors ${u.promo_requested && !u.promo_code ? 'bg-amber-950/20 ring-1 ring-amber-500/40' : (u.is_affiliate ? 'bg-purple-950/5' : '')}`}>
                          <td className="p-4">
                            <div className="flex items-center gap-3">
                              {u.avatar ? (
                                <img 
                                  src={`https://cdn.discordapp.com/avatars/${u.discord_id}/${u.avatar}.png`}
                                  alt="" 
                                  className="w-10 h-10 rounded-full"
                                />
                              ) : (
                                <div className="w-10 h-10 rounded-full bg-purple-600/30 flex items-center justify-center">
                                  <Users className="w-5 h-5 text-purple-400" />
                                </div>
                              )}
                              <div>
                                <p className="text-white font-medium">{u.username}</p>
                                <p className="text-gray-500 text-xs">{u.is_affiliate ? 'Affilié' : 'Non affilié'}</p>
                                {u.promo_requested && !u.promo_code && (
                                  <span data-testid={`promo-request-badge-${u.affiliate_code}`} className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-amber-300 bg-amber-500/15 border border-amber-400/40 rounded-full px-2 py-0.5 animate-pulse">
                                    🔔 A demandé un code promo
                                  </span>
                                )}
                                {u.promo_code && (
                                  <span data-testid={`promo-code-badge-${u.affiliate_code}`} className="mt-1 inline-flex items-center gap-1 text-[11px] font-mono text-fuchsia-300 bg-fuchsia-500/15 border border-fuchsia-400/30 rounded-full px-2 py-0.5">
                                    🏷️ {u.promo_code} -{u.promo_discount_percent || 5}%
                                  </span>
                                )}
                                {u.is_affiliate && u.paypal && (
                                  <button
                                    onClick={() => { navigator.clipboard.writeText(u.paypal); }}
                                    title="Copier le PayPal"
                                    data-testid={`affiliate-paypal-${u.affiliate_code}`}
                                    className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-blue-400 hover:text-blue-300 font-mono"
                                  >
                                    <Copy className="w-2.5 h-2.5" />
                                    {u.paypal}
                                  </button>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="p-4 text-center">
                            {u.affiliate_code ? (
                              <code className="px-2 py-1 bg-purple-600/20 border border-purple-600/30 rounded text-purple-400 font-mono text-xs">
                                {u.affiliate_code}
                              </code>
                            ) : (
                              <span className="text-gray-600">-</span>
                            )}
                          </td>
                          <td className="p-4 text-center">
                            {u.is_affiliate ? (
                              <select
                                value={u.commission_rate || 10}
                                onChange={(e) => updateAffiliateRate(u.id, e.target.value)}
                                className="px-2 py-1 bg-zinc-800 border border-purple-900/30 rounded text-purple-400 text-xs focus:outline-none"
                              >
                                <option value="5">5%</option>
                                <option value="10">10%</option>
                                <option value="15">15%</option>
                                <option value="20">20%</option>
                                <option value="25">25%</option>
                              </select>
                            ) : (
                              <span className="text-gray-600">-</span>
                            )}
                          </td>
                          <td className="p-4 text-center">
                            {u.is_affiliate ? (
                              <span className="text-cyan-400 font-semibold" data-testid={`affiliate-clicks-${u.affiliate_code}`}>{u.clicks || 0}</span>
                            ) : (
                              <span className="text-gray-600">-</span>
                            )}
                          </td>
                          <td className="p-4 text-center">
                            {u.is_affiliate ? (
                              <span className="text-emerald-400 font-semibold" title="Personnes connectées (arrivées sur le Discord) via le lien" data-testid={`affiliate-signups-${u.affiliate_code}`}>{u.signups || 0}</span>
                            ) : (
                              <span className="text-gray-600">-</span>
                            )}
                          </td>
                          <td className="p-4 text-center">
                            {u.is_affiliate ? (
                              <span className="text-white font-semibold">{u.total_sales || 0}</span>
                            ) : (
                              <span className="text-gray-600">-</span>
                            )}
                          </td>
                          <td className="p-4 text-center">
                            {u.is_affiliate ? (
                              <span className="text-gray-400 text-sm">{(u.total_commission || 0).toFixed(2)}€</span>
                            ) : (
                              <span className="text-gray-600">-</span>
                            )}
                          </td>
                          <td className="p-4 text-center">
                            {u.is_affiliate ? (
                              <span className={`font-semibold ${(u.pending_commission || 0) > 0 ? 'text-orange-400' : 'text-gray-500'}`}>
                                {(u.pending_commission || 0).toFixed(2)}€
                              </span>
                            ) : (
                              <span className="text-gray-600">-</span>
                            )}
                          </td>
                          <td className="p-4 text-center">
                            {u.is_affiliate ? (
                              <span className="text-green-400 text-sm">{(u.paid_commission || 0).toFixed(2)}€</span>
                            ) : (
                              <span className="text-gray-600">-</span>
                            )}
                          </td>
                          <td className="p-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              {u.is_affiliate && (
                                <button
                                  onClick={() => openAffiliateDetails(u.affiliate_code)}
                                  className="px-2 py-1.5 rounded-lg text-xs font-medium bg-cyan-600/20 hover:bg-cyan-600 text-cyan-400 hover:text-white transition-colors"
                                  title="Voir les détails"
                                  data-testid={`affiliate-details-btn-${u.affiliate_code}`}
                                >
                                  Détails
                                </button>
                              )}
                              {u.is_affiliate && (u.pending_commission || 0) > 0 && (
                                <button
                                  onClick={() => payAffiliate(u.id, u.username, u.pending_commission, u.affiliate_code)}
                                  disabled={affiliateLoading[u.affiliate_code]}
                                  className="px-2 py-1.5 rounded-lg text-xs font-medium bg-green-600/20 hover:bg-green-600 text-green-400 hover:text-white transition-colors disabled:opacity-50"
                                  title="Marquer comme payé"
                                >
                                  Payer
                                </button>
                              )}
                              {u.is_affiliate && (
                                u.promo_code ? (
                                  <button
                                    onClick={() => removeAffiliatePromo(u.affiliate_code)}
                                    className="px-2 py-1.5 rounded-lg text-xs font-medium bg-fuchsia-600/20 hover:bg-red-600 text-fuchsia-300 hover:text-white transition-colors"
                                    title="Retirer le code promo"
                                    data-testid={`remove-promo-btn-${u.affiliate_code}`}
                                  >
                                    🏷️ Retirer promo
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => assignAffiliatePromo(u.affiliate_code)}
                                    className={`px-2 py-1.5 rounded-lg text-xs font-medium transition-colors ${u.promo_requested ? 'bg-amber-500 hover:bg-amber-400 text-black' : 'bg-fuchsia-600/20 hover:bg-fuchsia-600 text-fuchsia-300 hover:text-white'}`}
                                    title="Créer / attribuer un code promo"
                                    data-testid={`assign-promo-btn-${u.affiliate_code}`}
                                  >
                                    🏷️ {u.promo_requested ? 'Attribuer le code' : 'Créer un code'}
                                  </button>
                                )
                              )}
                              <button
                                onClick={() => toggleUserAffiliate(u.id, u.username, u.is_affiliate)}
                                disabled={affiliateLoading[u.id]}
                                className={`px-2 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                  u.is_affiliate
                                    ? 'bg-red-600/20 hover:bg-red-600 text-red-400 hover:text-white'
                                    : 'bg-purple-600/20 hover:bg-purple-600 text-purple-400 hover:text-white'
                                } disabled:opacity-50`}
                              >
                                {affiliateLoading[u.id] ? (
                                  <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                                ) : u.is_affiliate ? (
                                  'Retirer'
                                ) : (
                                  'Ajouter'
                                )}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Info Box */}
            <div className="bg-purple-950/20 border border-purple-800/30 rounded-xl p-4">
              <h4 className="text-purple-400 font-semibold mb-2 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                Comment ça marche ?
              </h4>
              <ul className="text-gray-400 text-sm space-y-2">
                <li>• Clique sur <strong className="text-purple-400">"Ajouter"</strong> pour attribuer le rôle affilié à un utilisateur</li>
                <li>• Un <strong className="text-white">code unique</strong> sera généré automatiquement basé sur son pseudo</li>
                <li>• L'affilié peut consulter ses stats sur <strong className="text-cyan-400">/mon-affiliation</strong></li>
                <li>• Tu peux ajuster le <strong className="text-purple-400">taux de commission</strong> pour chaque affilié</li>
              </ul>
            </div>
          </div>
        )}

        {/* Suggestions Tab */}
        {activeTab === 'suggestions' && (
          <div className="space-y-4">
            <div className="bg-gradient-to-r from-amber-950/40 to-orange-950/20 border border-amber-500/30 rounded-xl p-4 md:p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-amber-500/20 rounded-xl flex items-center justify-center">
                  <Lightbulb className="w-5 h-5 text-amber-300" />
                </div>
                <div>
                  <h3 className="text-lg md:text-xl font-bold text-white">Suggestions de la communauté</h3>
                  <p className="text-gray-400 text-sm">
                    {suggestions.filter(s => s.status === 'new').length} nouvelle{suggestions.filter(s => s.status === 'new').length > 1 ? 's' : ''} ·
                    {' '}{suggestions.length} au total
                  </p>
                </div>
              </div>
            </div>

            {/* Filters */}
            {suggestions.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                {[
                  { id: 'all',   label: 'Toutes',          count: suggestions.length },
                  { id: 'new',   label: 'Nouvelles',       count: suggestions.filter(s => s.status === 'new').length },
                  { id: 'seen',  label: 'Lues',            count: suggestions.filter(s => s.status === 'seen').length },
                  { id: 'done',  label: 'Fait',            count: suggestions.filter(s => s.status === 'done').length },
                  { id: 'discord',      label: '💬 Discord',     count: suggestions.filter(s => s.category === 'discord').length },
                  { id: 'website',      label: '🌐 Site',        count: suggestions.filter(s => s.category === 'website').length },
                  { id: 'optimization', label: '⚙️ Opti',        count: suggestions.filter(s => s.category === 'optimization').length },
                  { id: 'other',        label: '✨ Autre',       count: suggestions.filter(s => s.category === 'other').length },
                ].map(f => (
                  <button
                    key={f.id}
                    onClick={() => setSuggestionFilter(f.id)}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                      suggestionFilter === f.id
                        ? 'bg-amber-500/20 border border-amber-500/40 text-amber-200'
                        : 'bg-zinc-900 border border-zinc-800 text-gray-400 hover:text-white'
                    }`}
                  >
                    {f.label} <span className="ml-1 opacity-70 tabular-nums">{f.count}</span>
                  </button>
                ))}
              </div>
            )}

            {suggestions.length === 0 ? (
              <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-12 text-center">
                <Lightbulb className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400">Aucune suggestion pour l'instant — les idées arrivent bientôt !</p>
              </div>
            ) : (
              <div className="space-y-3">
                {suggestions
                  .filter(s => {
                    if (suggestionFilter === 'all') return true;
                    if (['new', 'seen', 'done'].includes(suggestionFilter)) return s.status === suggestionFilter;
                    return s.category === suggestionFilter;
                  })
                  .map(s => {
                    const cats = {
                      discord:      { label: 'Discord',          color: 'indigo',  icon: MessageSquare },
                      website:      { label: 'Site web',         color: 'cyan',    icon: Globe },
                      optimization: { label: 'Optimisations',    color: 'emerald', icon: Cpu },
                      other:        { label: 'Autre',            color: 'purple',  icon: MoreHorizontal },
                    };
                    const cat = cats[s.category] || cats.other;
                    const accentMap = {
                      indigo:  'border-indigo-500/30  bg-indigo-500/5  text-indigo-300',
                      cyan:    'border-cyan-500/30    bg-cyan-500/5    text-cyan-300',
                      emerald: 'border-emerald-500/30 bg-emerald-500/5 text-emerald-300',
                      purple:  'border-purple-500/30  bg-purple-500/5  text-purple-300',
                    }[cat.color];
                    const CatIcon = cat.icon;
                    const statusColor = {
                      new:  'bg-amber-500/20  text-amber-300  border-amber-500/40',
                      seen: 'bg-zinc-700/40   text-zinc-300   border-zinc-600',
                      done: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40',
                    }[s.status] || '';
                    const statusLabel = { new: 'NOUVELLE', seen: 'LUE', done: 'FAIT' }[s.status] || s.status;
                    return (
                      <div
                        key={s.id}
                        data-testid={`suggestion-${s.id}`}
                        className={`bg-zinc-900/50 border ${s.status === 'new' ? 'border-amber-500/30' : 'border-zinc-800'} rounded-xl p-4 md:p-5 transition-colors`}
                      >
                        <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                          <div className="flex items-center gap-2 flex-wrap">
                            {s.avatar ? (
                              <img src={`https://cdn.discordapp.com/avatars/${s.discord_id}/${s.avatar}.png`} alt="" className="w-7 h-7 rounded-full" />
                            ) : (
                              <div className="w-7 h-7 rounded-full bg-zinc-700 flex items-center justify-center"><Users className="w-3.5 h-3.5 text-gray-400" /></div>
                            )}
                            <span className="text-white font-semibold text-sm">{s.username}</span>
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${accentMap}`}>
                              <CatIcon className="w-3 h-3" /> {cat.label}
                            </span>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${statusColor}`}>
                              {statusLabel}
                            </span>
                            <span className="text-gray-500 text-[11px]">
                              {new Date(s.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                        </div>
                        <h4 className="text-white font-bold text-base md:text-lg leading-tight mb-1.5">{s.title}</h4>
                        <p className="text-gray-300 text-sm leading-relaxed whitespace-pre-wrap">{s.message}</p>
                        <div className="flex items-center gap-2 mt-4 flex-wrap">
                          {s.status !== 'seen' && (
                            <button
                              onClick={() => updateSuggestionStatus(s.id, 'seen')}
                              className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-zinc-800 hover:bg-zinc-700 text-gray-300 transition-colors"
                            >
                              Marquer "Lue"
                            </button>
                          )}
                          {s.status !== 'done' && (
                            <button
                              onClick={() => updateSuggestionStatus(s.id, 'done')}
                              data-testid={`suggestion-done-${s.id}`}
                              className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-emerald-600/20 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-600/30 transition-colors inline-flex items-center gap-1.5"
                            >
                              <Check className="w-3 h-3" /> Marquer "Fait"
                            </button>
                          )}
                          {s.status === 'done' && (
                            <button
                              onClick={() => updateSuggestionStatus(s.id, 'new')}
                              className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-zinc-800 hover:bg-zinc-700 text-gray-400 transition-colors"
                            >
                              Remettre à "Nouvelle"
                            </button>
                          )}
                          <button
                            onClick={() => deleteSuggestion(s.id)}
                            className="ml-auto px-3 py-1.5 text-xs font-semibold rounded-lg text-gray-500 hover:text-red-400 hover:bg-red-500/10 transition-colors inline-flex items-center gap-1.5"
                          >
                            <Trash2 className="w-3 h-3" /> Supprimer
                          </button>
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        )}

        {/* Support Tickets Tab */}
        {activeTab === 'support' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-white flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-blue-400" />
                  Tickets Support
                </h2>
                <p className="text-gray-400 text-sm mt-0.5">
                  {supportTickets.filter(t => t.status === 'open').length} ouverts · {supportTickets.length} total
                </p>
              </div>
              <button onClick={fetchSupportTickets} className="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-gray-300 text-xs rounded-lg transition-colors">
                Rafraîchir
              </button>
            </div>

            {supportLoading ? (
              <div className="flex justify-center py-12">
                <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : supportTickets.length === 0 ? (
              <div className="bg-zinc-900/50 border border-blue-900/30 rounded-xl p-12 text-center">
                <MessageSquare className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400">Aucun ticket pour l'instant</p>
              </div>
            ) : (
              <div className="space-y-3">
                {supportTickets.map(ticket => {
                  const statusCfg = {
                    open:        { label: 'Ouvert',    color: 'text-yellow-400', bg: 'border-yellow-500/30 bg-yellow-500/5' },
                    in_progress: { label: 'En cours',  color: 'text-cyan-400',   bg: 'border-cyan-500/30 bg-cyan-500/5' },
                    closed:      { label: 'Résolu',    color: 'text-green-400',  bg: 'border-green-500/30 bg-green-500/5' },
                  };
                  const catLabels = { sav: 'SAV', commande: 'Commande', technique: 'Technique', autre: 'Autre' };
                  const s = statusCfg[ticket.status] || statusCfg.open;
                  const isExpanded = expandedTicket === ticket.id;
                  return (
                    <div key={ticket.id} data-testid={`support-ticket-${ticket.id}`}
                      className={`border rounded-xl overflow-hidden ${s.bg}`}>
                      {/* Header */}
                      <button onClick={() => setExpandedTicket(isExpanded ? null : ticket.id)}
                        className="w-full px-4 py-3.5 flex items-center gap-3 text-left">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-gray-400 text-xs font-mono">#{ticket.ticket_number}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full border ${s.bg} ${s.color}`}>{s.label}</span>
                            <span className="text-xs text-gray-500 bg-zinc-800/50 px-2 py-0.5 rounded-full">{catLabels[ticket.category] || ticket.category}</span>
                          </div>
                          <p className="text-white text-sm font-medium mt-0.5 truncate">{ticket.subject}</p>
                          <p className="text-gray-500 text-xs mt-0.5">{ticket.username} · {new Date(ticket.created_at).toLocaleDateString('fr-FR')}</p>
                        </div>
                        {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-500 shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-500 shrink-0" />}
                      </button>

                      {isExpanded && (
                        <div className="border-t border-white/5 px-4 py-4 space-y-4">
                          <div className="bg-black/20 rounded-lg p-3">
                            <p className="text-gray-400 text-xs font-medium mb-1.5">Description</p>
                            <p className="text-gray-200 text-sm whitespace-pre-wrap">{ticket.description}</p>
                          </div>

                          {ticket.userdiag_link && (
                            <div className="flex items-center gap-2 p-2.5 bg-cyan-900/20 border border-cyan-800/30 rounded-lg">
                              <MessageSquare className="w-4 h-4 text-cyan-400 shrink-0" />
                              <span className="text-xs text-gray-400 shrink-0">UserDiag :</span>
                              <a href={ticket.userdiag_link} target="_blank" rel="noreferrer"
                                className="text-xs text-cyan-400 hover:text-cyan-300 underline truncate flex items-center gap-1.5 min-w-0">
                                <span className="truncate">{ticket.userdiag_link}</span>
                                <ExternalLink className="w-3 h-3 shrink-0" />
                              </a>
                            </div>
                          )}

                          {/* Admin note */}
                          <div>
                            <label className="block text-xs text-gray-400 mb-1.5">Réponse / Note (envoyée à l'utilisateur)</label>
                            <div className="flex gap-2">
                              <input type="text"
                                placeholder="Ex: Problème résolu, vérifiez votre gestionnaire de tâches..."
                                value={supportNotes[ticket.id] !== undefined ? supportNotes[ticket.id] : (ticket.admin_note || '')}
                                onChange={e => setSupportNotes(p => ({ ...p, [ticket.id]: e.target.value }))}
                                className="flex-1 bg-zinc-950 border border-blue-900/30 rounded-lg px-3 py-2 text-white text-xs focus:outline-none focus:border-blue-500"
                                data-testid={`support-note-${ticket.id}`} />
                              <button onClick={() => updateSupportTicket(ticket.id, ticket.status, supportNotes[ticket.id] ?? ticket.admin_note ?? '')}
                                className="px-3 py-2 bg-zinc-700 hover:bg-zinc-600 text-white text-xs rounded-lg transition-colors">
                                Sauv.
                              </button>
                            </div>
                          </div>

                          {/* Status buttons */}
                          <div className="flex flex-wrap gap-2">
                            {ticket.status !== 'in_progress' && (
                              <button onClick={() => updateSupportTicket(ticket.id, 'in_progress', supportNotes[ticket.id] ?? ticket.admin_note ?? '')}
                                className="flex items-center gap-1.5 px-3 py-2 bg-cyan-600/80 hover:bg-cyan-600 text-white text-xs font-medium rounded-lg transition-colors"
                                data-testid={`ticket-inprogress-${ticket.id}`}>
                                <Clock className="w-3.5 h-3.5" /> En cours
                              </button>
                            )}
                            {ticket.status !== 'closed' && (
                              <button onClick={() => updateSupportTicket(ticket.id, 'closed', supportNotes[ticket.id] ?? ticket.admin_note ?? '')}
                                className="flex items-center gap-1.5 px-3 py-2 bg-green-600/80 hover:bg-green-600 text-white text-xs font-medium rounded-lg transition-colors"
                                data-testid={`ticket-close-${ticket.id}`}>
                                <CheckCircle className="w-3.5 h-3.5" /> Marquer résolu
                              </button>
                            )}
                            {ticket.status !== 'open' && (
                              <button onClick={() => updateSupportTicket(ticket.id, 'open')}
                                className="flex items-center gap-1.5 px-3 py-2 bg-zinc-700 hover:bg-zinc-600 text-gray-300 text-xs rounded-lg transition-colors">
                                Réouvrir
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Admins Tab */}
        {activeTab === 'admins' && (
          <div className="space-y-5" data-testid="admins-tab-content">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-white flex items-center gap-2">
                  <UserCog className="w-5 h-5 text-fuchsia-400" />
                  Administrateurs
                </h2>
                <p className="text-gray-400 text-sm mt-0.5">
                  {adminsList.total} admin(s) au total — {adminsList.manual_admins.length} manuel(s) · {adminsList.discord_admins.length} via Discord
                </p>
              </div>
              <button onClick={fetchAdmins} className="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-gray-300 text-xs rounded-lg transition-colors">
                Rafraîchir
              </button>
            </div>

            {/* Add admin card */}
            <div className="bg-zinc-900/60 border border-fuchsia-900/30 rounded-xl p-5">
              <h3 className="text-white font-semibold text-sm mb-1 flex items-center gap-2">
                <Plus className="w-4 h-4 text-fuchsia-400" />
                Ajouter un administrateur
              </h3>
              <p className="text-zinc-500 text-xs mb-4">
                Donne l&apos;accès au panel admin à un utilisateur via son ID Discord (clic droit sur un membre &gt; Copier l&apos;identifiant).
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">ID Discord</label>
                  <input
                    type="text"
                    value={adminAddId}
                    onChange={(e) => { setAdminAddId(e.target.value.replace(/\D/g, '')); setAdminAddError(''); }}
                    placeholder="123456789012345678"
                    data-testid="admin-add-id-input"
                    className="w-full bg-black/40 border border-zinc-700 focus:border-fuchsia-500 outline-none rounded-lg px-3 py-2 text-white text-sm placeholder-zinc-600"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">Note (optionnelle)</label>
                  <input
                    type="text"
                    value={adminAddNote}
                    onChange={(e) => setAdminAddNote(e.target.value)}
                    placeholder="Ex: Co-fondateur, gérant SAV…"
                    data-testid="admin-add-note-input"
                    maxLength={120}
                    className="w-full bg-black/40 border border-zinc-700 focus:border-fuchsia-500 outline-none rounded-lg px-3 py-2 text-white text-sm placeholder-zinc-600"
                  />
                </div>
              </div>

              {adminAddLookup && (
                <div className="mt-3 flex items-center gap-3 bg-black/40 border border-zinc-800 rounded-lg p-3">
                  {adminAddLookup.avatar_url ? (
                    <img src={adminAddLookup.avatar_url} alt="" className="w-10 h-10 rounded-full" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center text-zinc-500">
                      <UserCog className="w-5 h-5" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium truncate">
                      {adminAddLookup.username || 'Utilisateur inconnu'}
                    </p>
                    <p className="text-xs text-zinc-500">
                      {adminAddLookup.is_member ? '✅ Membre du serveur Discord' : adminAddLookup.found ? '⚠️ Présent en DB mais pas sur le serveur' : '❌ Introuvable sur Discord & DB'}
                    </p>
                  </div>
                </div>
              )}

              {adminAddError && (
                <div className="mt-3 text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
                  {adminAddError}
                </div>
              )}

              <button
                onClick={addAdmin}
                disabled={adminAddSubmitting || !/^\d{15,25}$/.test(adminAddId.trim())}
                data-testid="admin-add-submit-btn"
                className="mt-4 w-full md:w-auto px-5 py-2 bg-fuchsia-600 hover:bg-fuchsia-500 disabled:bg-zinc-800 disabled:text-zinc-500 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                {adminAddSubmitting ? (
                  <div className="w-4 h-4 border-2 border-white/50 border-t-white rounded-full animate-spin" />
                ) : (
                  <Plus className="w-4 h-4" />
                )}
                Ajouter comme admin
              </button>
            </div>

            {/* Manual Admins List */}
            <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-5">
              <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2">
                <Shield className="w-4 h-4 text-fuchsia-400" />
                Admins ajoutés manuellement
                <span className="text-xs text-zinc-500 font-normal">({adminsList.manual_admins.length})</span>
              </h3>
              {adminsLoading ? (
                <div className="flex justify-center py-8">
                  <div className="w-6 h-6 border-2 border-fuchsia-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : adminsList.manual_admins.length === 0 ? (
                <p className="text-zinc-500 text-sm italic">Aucun admin manuel pour l&apos;instant.</p>
              ) : (
                <div className="space-y-2">
                  {adminsList.manual_admins.map((a) => (
                    <div key={a.discord_id} className="flex items-center gap-3 bg-black/30 border border-zinc-800 rounded-lg p-3" data-testid={`manual-admin-${a.discord_id}`}>
                      {a.avatar_url ? (
                        <img src={a.avatar_url} alt="" className="w-10 h-10 rounded-full" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center text-zinc-500">
                          <Shield className="w-5 h-5" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium truncate">{a.username}</p>
                        <p className="text-xs text-zinc-500 font-mono">{a.discord_id}</p>
                        {a.note ? <p className="text-xs text-zinc-400 italic mt-0.5 truncate">{`"${a.note}"`}</p> : null}
                        {a.added_by && (
                          <p className="text-[10px] text-zinc-600 mt-0.5">Ajouté par {a.added_by}</p>
                        )}
                      </div>
                      <button
                        onClick={() => removeAdmin(a.discord_id, a.username)}
                        data-testid={`admin-remove-${a.discord_id}`}
                        className="px-3 py-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/30 text-xs font-medium rounded-lg transition-colors flex items-center gap-1.5"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        Retirer
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Discord-detected Admins (read-only) */}
            <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-5">
              <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2">
                <Shield className="w-4 h-4 text-cyan-400" />
                Admins via rôle Discord
                <span className="text-xs text-zinc-500 font-normal">({adminsList.discord_admins.length})</span>
              </h3>
              <p className="text-zinc-500 text-xs mb-3">
                Ces utilisateurs sont admins automatiquement car ils possèdent le rôle Admin du serveur ou en sont le propriétaire. Pour les retirer, modifie leurs rôles directement sur Discord.
              </p>
              {adminsList.discord_admins.length === 0 ? (
                <p className="text-zinc-500 text-sm italic">Aucun admin détecté via Discord.</p>
              ) : (
                <div className="space-y-2">
                  {adminsList.discord_admins.map((a) => (
                    <div key={a.discord_id} className="flex items-center gap-3 bg-black/30 border border-zinc-800 rounded-lg p-3" data-testid={`discord-admin-${a.discord_id}`}>
                      {a.avatar_url ? (
                        <img src={a.avatar_url} alt="" className="w-10 h-10 rounded-full" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center text-zinc-500">
                          <Shield className="w-5 h-5" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium truncate">{a.username}</p>
                        <p className="text-xs text-zinc-500 font-mono">{a.discord_id}</p>
                        <span className={`text-[10px] mt-1 inline-block px-2 py-0.5 rounded ${a.source === 'server_owner' ? 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/30' : 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/30'}`}>
                          {a.source === 'server_owner' ? '👑 Propriétaire serveur' : '🛡️ Rôle Admin Discord'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Client Discount Tab — Promos Clients */}
        {activeTab === 'client-discount' && (
          <div className="space-y-5" data-testid="client-discount-tab">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-white flex items-center gap-2">
                  <Crown className="w-5 h-5 text-green-400" />
                  Promos Clients
                </h2>
                <p className="text-gray-400 text-sm mt-0.5">
                  Réductions exclusives pour les users avec le rôle Discord <span className="text-green-400 font-medium">Clients</span>.
                </p>
              </div>
              <button
                onClick={saveClientDiscount}
                disabled={clientDiscountSaving}
                data-testid="client-discount-save-btn"
                className="px-4 py-2 bg-green-600 hover:bg-green-500 disabled:bg-zinc-800 disabled:text-zinc-500 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2"
              >
                {clientDiscountSaving ? <div className="w-4 h-4 border-2 border-white/50 border-t-white rounded-full animate-spin" /> : <Check className="w-4 h-4" />}
                {clientDiscountSaved ? 'Sauvegardé ✓' : 'Sauvegarder'}
              </button>
            </div>

            {/* Master toggle */}
            <div className="bg-zinc-900/60 border border-green-900/30 rounded-xl p-5">
              <label className="flex items-center justify-between cursor-pointer">
                <div>
                  <p className="text-white font-semibold">Activer les promos clients</p>
                  <p className="text-zinc-500 text-xs mt-0.5">Active/désactive toutes les réductions clients en un clic.</p>
                </div>
                <input
                  type="checkbox"
                  data-testid="client-discount-master-toggle"
                  checked={clientDiscount.enabled || false}
                  onChange={(e) => setClientDiscount({ ...clientDiscount, enabled: e.target.checked })}
                  className="w-5 h-5 accent-green-500"
                />
              </label>
            </div>

            {/* Per-service rules — grouped by category */}
            <div className={`space-y-6 ${!clientDiscount.enabled ? 'opacity-40 pointer-events-none' : ''}`}>
              {[
                { groupKey: 'services', title: 'Services', subtitle: 'Réductions sur les services principaux' },
                { groupKey: 'licences', title: 'Licences', subtitle: 'Choisis quelle(s) licence(s) ont une réduction (au cas par cas)' },
              ].map(({ groupKey, title, subtitle }) => {
                const entries = Object.entries(SERVICE_LABELS).filter(([, m]) => m.group === groupKey);
                const allEnabled = entries.every(([k]) => clientDiscount.rules?.[k]?.enabled);
                const toggleAll = (enabled) => {
                  const newRules = { ...(clientDiscount.rules || {}) };
                  entries.forEach(([k]) => {
                    newRules[k] = { ...(newRules[k] || { type: 'percent', value: 0 }), enabled };
                  });
                  setClientDiscount({ ...clientDiscount, rules: newRules });
                };
                return (
                  <div key={groupKey}>
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <h3 className="text-white font-semibold text-sm uppercase tracking-wider">{title}</h3>
                        <p className="text-zinc-500 text-xs">{subtitle}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => toggleAll(!allEnabled)}
                        data-testid={`client-discount-toggle-group-${groupKey}`}
                        className="text-[11px] px-2.5 py-1 rounded-md border border-zinc-700 bg-zinc-900 hover:bg-zinc-800 text-zinc-300"
                      >
                        {allEnabled ? 'Tout désactiver' : 'Tout activer'}
                      </button>
                    </div>
                    <div className="space-y-2">
                      {entries.map(([key, meta]) => {
                        const rule = clientDiscount.rules?.[key] || { enabled: false, type: 'percent', value: 0 };
                        const updateRule = (patch) => setClientDiscount({
                          ...clientDiscount,
                          rules: { ...(clientDiscount.rules || {}), [key]: { ...rule, ...patch } }
                        });
                        const numVal = parseFloat(String(rule.value).replace(',', '.')) || 0;
                        const computedPrice = meta.basePrice ? (
                          rule.type === 'percent'
                            ? (meta.basePrice * (1 - numVal / 100))
                            : Math.max(0, meta.basePrice - numVal)
                        ) : null;
                        return (
                          <div key={key} data-testid={`client-discount-rule-${key}`} className={`bg-zinc-900/60 border ${rule.enabled ? 'border-green-700/40' : 'border-zinc-800'} rounded-xl p-4 transition-colors`}>
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-3">
                                <input
                                  type="checkbox"
                                  data-testid={`client-discount-toggle-${key}`}
                                  checked={rule.enabled}
                                  onChange={(e) => updateRule({ enabled: e.target.checked })}
                                  className="w-5 h-5 accent-green-500"
                                />
                                <div>
                                  <p className="text-white font-medium text-sm">{meta.label}</p>
                                  {meta.basePrice && <p className="text-zinc-500 text-xs">Prix de base : {meta.basePrice}€</p>}
                                </div>
                              </div>
                              {rule.enabled && computedPrice !== null && (
                                <div className="text-right">
                                  <p className="text-zinc-500 text-xs line-through">{meta.basePrice}€</p>
                                  <p className="text-green-400 font-bold text-sm">{computedPrice.toFixed(2)}€</p>
                                </div>
                              )}
                            </div>
                            <div className={`flex items-center gap-2 ${!rule.enabled ? 'opacity-40 pointer-events-none' : ''}`}>
                              <select
                                value={rule.type || 'percent'}
                                onChange={(e) => updateRule({ type: e.target.value })}
                                data-testid={`client-discount-type-${key}`}
                                className="bg-black/40 border border-zinc-700 rounded px-2 py-1.5 text-xs text-white"
                              >
                                <option value="percent">% (pourcentage)</option>
                                <option value="fixed">€ (montant fixe)</option>
                              </select>
                              <input
                                type="text"
                                inputMode="decimal"
                                value={rule.value ?? 0}
                                onChange={(e) => updateRule({ value: e.target.value })}
                                placeholder="Ex : 2,5"
                                data-testid={`client-discount-value-${key}`}
                                className="flex-1 bg-black/40 border border-zinc-700 rounded px-3 py-1.5 text-sm text-white"
                              />
                              <span className="text-zinc-500 text-sm w-6">{rule.type === 'percent' ? '%' : '€'}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-3 text-xs text-blue-300">
              💡 Les réductions s'appliquent <b>uniquement</b> pour les utilisateurs avec le rôle Discord <b>Clients</b>. Tu peux activer la réduc seulement sur certaines licences ou certains services au cas par cas.
            </div>
          </div>
        )}

        {/* Recruitment Tab */}
        {activeTab === 'recruitment' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-white flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-cyan-400" />
                  Candidatures Helper
                </h2>
                <p className="text-gray-400 text-sm mt-0.5">
                  {helperApplications.filter(a => a.status === 'pending').length} en attente · {helperApplications.length} total
                </p>
              </div>
              <button onClick={fetchHelperApplications} className="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-gray-300 text-xs rounded-lg transition-colors">
                Rafraîchir
              </button>
            </div>

            {/* Gestion par poste */}
            <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-5">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-white font-semibold flex items-center gap-2 text-sm">
                  <Briefcase className="w-4 h-4 text-cyan-400" />
                  Gestion des candidatures par poste
                </h3>
                {savingRecruitmentSettings && (
                  <span className="text-xs text-cyan-400 inline-flex items-center gap-1.5">
                    <div className="w-3 h-3 border-2 border-cyan-500/30 border-t-cyan-400 rounded-full animate-spin" />
                    Sauvegarde…
                  </span>
                )}
              </div>
              <p className="text-zinc-500 text-xs mb-4 leading-relaxed">
                <strong className="text-zinc-400">Visible</strong> : carte affichée sur la page de recrutement ·{' '}
                <strong className="text-zinc-400">Ouvert</strong> : accepte de nouvelles candidatures
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                {[
                  { id: 'helper', label: 'Helper Discord', color: 'cyan' },
                  { id: 'community_manager', label: 'Community Manager', color: 'pink' },
                  { id: 'tester', label: 'Bêta-Testeur', color: 'amber' },
                  { id: 'content_creator', label: 'Content Creator', color: 'purple' },
                ].map(({ id, label, color }) => {
                  const cfg = recruitmentSettings[id] || { open: true, visible: true };
                  const borderColor = { cyan: 'border-cyan-500/25', pink: 'border-pink-500/25', amber: 'border-amber-500/25', purple: 'border-purple-500/25' }[color];
                  const textColor = { cyan: 'text-cyan-300', pink: 'text-pink-300', amber: 'text-amber-300', purple: 'text-purple-300' }[color];
                  return (
                    <div key={id} className={`bg-black/30 border ${borderColor} rounded-lg p-4`} data-testid={`recruitment-role-${id}`}>
                      <p className={`text-sm font-semibold ${textColor} mb-3`}>{label}</p>
                      <div className="space-y-2.5">
                        <ToggleRow
                          label="Visible"
                          checked={cfg.visible}
                          onChange={() => toggleRoleFlag(id, 'visible')}
                          dataTestId={`toggle-visible-${id}`}
                        />
                        <ToggleRow
                          label="Ouvert"
                          checked={cfg.open}
                          onChange={() => toggleRoleFlag(id, 'open')}
                          dataTestId={`toggle-open-${id}`}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            {helperLoading ? (
              <div className="flex justify-center py-12">
                <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : helperApplications.length === 0 ? (
              <div className="bg-zinc-900/50 border border-cyan-900/30 rounded-xl p-12 text-center">
                <Briefcase className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400">Aucune candidature pour l'instant</p>
              </div>
            ) : (
              <>
              {/* === ÉQUIPE ACTUELLE (approved members) — grouped by role === */}
              {(() => {
                const approved = helperApplications.filter(a => a.status === 'approved');
                if (approved.length === 0) return null;

                const roles = [
                  { id: 'helper',            label: 'Helpers',           accent: 'cyan',   icon: '🛠' },
                  { id: 'community_manager', label: 'Community Managers', accent: 'pink',   icon: '📢' },
                  { id: 'tester',            label: 'Bêta-Testeurs',     accent: 'amber',  icon: '🧪' },
                  { id: 'content_creator',   label: 'Content Creators',  accent: 'purple', icon: '🎥' },
                ];

                return (
                  <div data-testid="recruitment-team-panel" className="bg-zinc-900/40 border border-emerald-900/30 rounded-xl p-4 md:p-5 mb-4">
                    <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                      <h3 className="text-white font-semibold flex items-center gap-2 text-sm md:text-base">
                        <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-emerald-400" />
                        Équipe actuelle
                      </h3>
                      <span className="text-emerald-300/80 text-xs font-medium">
                        {approved.length} membre{approved.length > 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {roles.map(role => {
                        const members = approved.filter(a => (a.role_type || 'helper') === role.id);
                        if (members.length === 0) return null;
                        const accentMap = {
                          cyan:   { bd: 'border-cyan-500/25',   bg: 'bg-cyan-500/5',   tx: 'text-cyan-300' },
                          pink:   { bd: 'border-pink-500/25',   bg: 'bg-pink-500/5',   tx: 'text-pink-300' },
                          amber:  { bd: 'border-amber-500/25',  bg: 'bg-amber-500/5',  tx: 'text-amber-300' },
                          purple: { bd: 'border-purple-500/25', bg: 'bg-purple-500/5', tx: 'text-purple-300' },
                        }[role.accent];
                        return (
                          <div key={role.id} className={`border ${accentMap.bd} ${accentMap.bg} rounded-lg p-3`}>
                            <div className="flex items-center justify-between mb-2">
                              <p className={`text-xs font-bold uppercase tracking-wider ${accentMap.tx} flex items-center gap-1.5`}>
                                <span>{role.icon}</span>
                                {role.label}
                              </p>
                              <span className={`text-[10px] font-bold tabular-nums ${accentMap.tx}`}>
                                {members.length}
                              </span>
                            </div>
                            <div className="space-y-1.5">
                              {members.map(m => (
                                <div key={m.id} className="flex items-center justify-between gap-2 px-2 py-1.5 rounded bg-black/30 group">
                                  <div className="flex items-center gap-2 min-w-0 flex-1">
                                    {m.avatar ? (
                                      <img src={`https://cdn.discordapp.com/avatars/${m.discord_id}/${m.avatar}.png`} alt="" className="w-6 h-6 rounded-full flex-shrink-0" />
                                    ) : (
                                      <div className="w-6 h-6 rounded-full bg-zinc-700 flex items-center justify-center flex-shrink-0">
                                        <Users className="w-3 h-3 text-gray-400" />
                                      </div>
                                    )}
                                    <span className="text-white text-sm truncate">{m.username}</span>
                                  </div>
                                  <button
                                    onClick={() => deleteHelperApplication(m.id)}
                                    title="Retirer du rôle"
                                    data-testid={`team-remove-${m.id}`}
                                    className="opacity-0 group-hover:opacity-100 p-1 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded transition-all flex-shrink-0"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              {/* === CANDIDATURES (only pending now — approved are above, rejected are auto-deleted) === */}
              {helperApplications.filter(a => a.status === 'pending').length === 0 ? (
                <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-8 text-center">
                  <Briefcase className="w-10 h-10 text-gray-600 mx-auto mb-2" />
                  <p className="text-gray-400 text-sm">Aucune candidature en attente</p>
                </div>
              ) : (
              <div className="space-y-3">
                {helperApplications.filter(a => a.status === 'pending').map((app) => {
                  const statusColors = { pending: 'border-yellow-500/40 bg-yellow-500/5', approved: 'border-green-500/40 bg-green-500/5', rejected: 'border-red-500/40 bg-red-500/5' };
                  const statusLabel = { pending: 'En attente', approved: 'Acceptée', rejected: 'Refusée' };
                  const statusTextColor = { pending: 'text-yellow-400', approved: 'text-green-400', rejected: 'text-red-400' };
                  return (
                    <div key={app.id} data-testid={`helper-app-${app.id}`}
                      className={`border rounded-xl p-4 md:p-5 ${statusColors[app.status] || 'border-cyan-900/30 bg-zinc-900/50'}`}>
                      <div className="flex items-start justify-between gap-3 mb-4">
                        <div className="flex items-center gap-3">
                          {app.avatar ? (
                            <img src={`https://cdn.discordapp.com/avatars/${app.discord_id}/${app.avatar}.png`} alt="" className="w-10 h-10 rounded-full" />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-cyan-600/30 flex items-center justify-center"><Users className="w-5 h-5 text-cyan-400" /></div>
                          )}
                          <div>
                            <p className="text-white font-semibold">{app.username}</p>
                            <p className="text-gray-500 text-xs">{app.age} ans · {new Date(app.created_at).toLocaleDateString('fr-FR')}</p>
                            <span className={`mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                              app.role_type === 'community_manager' ? 'bg-pink-500/15 text-pink-300 border border-pink-500/30' :
                              app.role_type === 'tester' ? 'bg-amber-500/15 text-amber-300 border border-amber-500/30' :
                              app.role_type === 'content_creator' ? 'bg-purple-500/15 text-purple-300 border border-purple-500/30' :
                              'bg-cyan-500/15 text-cyan-300 border border-cyan-500/30'
                            }`}>
                              {app.role_type === 'community_manager' ? 'Community Manager' :
                               app.role_type === 'tester' ? 'Bêta-Testeur' :
                               app.role_type === 'content_creator' ? 'Content Creator' :
                               'Helper'}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs font-medium px-2 py-1 rounded-full ${statusTextColor[app.status]} bg-black/20`}>{statusLabel[app.status] || app.status}</span>
                          <button onClick={() => deleteHelperApplication(app.id)} className="p-1.5 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors" data-testid={`delete-helper-${app.id}`} title="Supprimer"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4 text-sm">
                        <div className="bg-black/20 rounded-lg p-3">
                          <p className="text-gray-400 text-xs font-medium mb-1 flex items-center gap-1.5"><Wrench className="w-3 h-3" /> Compétences</p>
                          <p className="text-gray-200">{app.it_skills}</p>
                        </div>
                        <div className="bg-black/20 rounded-lg p-3">
                          <p className="text-gray-400 text-xs font-medium mb-1 flex items-center gap-1.5"><Clock className="w-3 h-3" /> Disponibilités</p>
                          <p className="text-gray-200">{app.availability}</p>
                        </div>
                        <div className="bg-black/20 rounded-lg p-3">
                          <p className="text-gray-400 text-xs font-medium mb-1">Expérience</p>
                          <p className="text-gray-200">{app.experience}</p>
                        </div>
                        <div className="bg-black/20 rounded-lg p-3">
                          <p className="text-gray-400 text-xs font-medium mb-1">Motivation</p>
                          <p className="text-gray-200">{app.motivation}</p>
                        </div>
                      </div>
                      {app.has_windows_install && (
                        <div className="flex items-center gap-2 mb-3 text-xs text-cyan-300"><CheckCircle className="w-4 h-4" /> Sait installer Windows 10/11</div>
                      )}
                      {app.role_type === 'tester' && app.can_reinstall_windows && (
                        <div className="flex items-center gap-2 mb-3 text-xs text-amber-300"><CheckCircle className="w-4 h-4" /> Autonome pour réinstaller Windows</div>
                      )}
                      {app.role_type === 'tester' && (app.games_played || app.pc_specs) && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4 text-sm">
                          {app.games_played && (
                            <div className="bg-amber-500/5 border border-amber-500/15 rounded-lg p-3">
                              <p className="text-amber-300/80 text-xs font-medium mb-1">Jeux principaux</p>
                              <p className="text-gray-200">{app.games_played}</p>
                            </div>
                          )}
                          {app.pc_specs && (
                            <div className="bg-amber-500/5 border border-amber-500/15 rounded-lg p-3">
                              <p className="text-amber-300/80 text-xs font-medium mb-1">Configuration PC</p>
                              <p className="text-gray-200">{app.pc_specs}</p>
                            </div>
                          )}
                        </div>
                      )}
                      {app.role_type === 'community_manager' && app.social_handles && (
                        <div className="bg-pink-500/5 border border-pink-500/15 rounded-lg p-3 mb-4 text-sm">
                          <p className="text-pink-300/80 text-xs font-medium mb-1">Réseaux sociaux</p>
                          <p className="text-gray-200 whitespace-pre-line">{app.social_handles}</p>
                        </div>
                      )}
                      {app.role_type === 'content_creator' && (app.creator_platform || app.creator_channel_url || app.creator_audience_size || app.creator_content_type) && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4 text-sm">
                          {app.creator_platform && (
                            <div className="bg-purple-500/5 border border-purple-500/15 rounded-lg p-3">
                              <p className="text-purple-300/80 text-xs font-medium mb-1">Plateforme</p>
                              <p className="text-gray-200 capitalize">{app.creator_platform}</p>
                            </div>
                          )}
                          {app.creator_audience_size && (
                            <div className="bg-purple-500/5 border border-purple-500/15 rounded-lg p-3">
                              <p className="text-purple-300/80 text-xs font-medium mb-1">Audience</p>
                              <p className="text-gray-200">{app.creator_audience_size}</p>
                            </div>
                          )}
                          {app.creator_channel_url && (
                            <div className="bg-purple-500/5 border border-purple-500/15 rounded-lg p-3 md:col-span-2">
                              <p className="text-purple-300/80 text-xs font-medium mb-1">Lien de la chaîne / profil</p>
                              <a href={app.creator_channel_url} target="_blank" rel="noopener noreferrer" className="text-purple-200 hover:text-purple-100 underline underline-offset-2 break-all">
                                {app.creator_channel_url}
                              </a>
                            </div>
                          )}
                          {app.creator_content_type && (
                            <div className="bg-purple-500/5 border border-purple-500/15 rounded-lg p-3 md:col-span-2">
                              <p className="text-purple-300/80 text-xs font-medium mb-1">Type de contenu</p>
                              <p className="text-gray-200 whitespace-pre-line">{app.creator_content_type}</p>
                            </div>
                          )}
                        </div>
                      )}
                      {app.value_proposition && (
                        <div className="bg-cyan-500/5 border border-cyan-500/15 rounded-lg p-3 mb-4 text-sm">
                          <p className="text-cyan-300/80 text-xs font-medium mb-1">Ce qu'il/elle apporte</p>
                          <p className="text-gray-200">{app.value_proposition}</p>
                        </div>
                      )}

                      {/* Voice message player */}
                      {app.has_voice_message && (
                        <VoiceMessagePlayer appId={app.id} duration={app.voice_message_duration} token={token} />
                      )}
                      {/* Fil de discussion avec le candidat (ses réponses MP au bot apparaissent ici) */}
                      {Array.isArray(app.staff_messages) && app.staff_messages.length > 0 && (
                        <div className="mb-3 p-3 rounded-lg bg-zinc-950/50 border border-zinc-800" data-testid={`candidate-thread-${app.id}`}>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-semibold text-gray-300">💬 Discussion</span>
                            <button onClick={fetchHelperApplications} className="text-[11px] text-cyan-400 hover:text-cyan-300 transition-colors">↻ Actualiser</button>
                          </div>
                          <div className="flex flex-col gap-1.5 max-h-56 overflow-y-auto pr-1">
                            {app.staff_messages.map((m, i) => {
                              const fromCandidate = m.from === 'candidate';
                              return (
                                <div key={i} className={`flex ${fromCandidate ? 'justify-start' : 'justify-end'}`}>
                                  <div className={`max-w-[80%] rounded-lg px-2.5 py-1.5 text-xs ${fromCandidate ? 'bg-zinc-800 text-gray-200' : 'bg-cyan-600/25 border border-cyan-600/40 text-cyan-50'}`}>
                                    <p className="whitespace-pre-wrap break-words">{m.message}</p>
                                    <p className="mt-0.5 text-[10px] text-gray-500">{fromCandidate ? (m.by || 'Candidat') : (m.by || 'Staff')}{m.at ? ' · ' + new Date(m.at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}</p>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      {/* Send a custom Discord DM to the candidate (ask questions before deciding) */}
                      <div className="mb-4 p-3 rounded-lg bg-cyan-950/30 border border-cyan-900/40">
                        <label className="block text-xs text-cyan-300 mb-1.5 font-semibold">💬 Envoyer un message au candidat (MP Discord)</label>
                        <textarea
                          rows={2}
                          placeholder="Ex: Salut ! Avant de décider, peux-tu me dire quelle est ta disponibilité exacte ?"
                          value={helperMessages[app.id] || ''}
                          onChange={(e) => setHelperMessages(prev => ({ ...prev, [app.id]: e.target.value }))}
                          className="w-full bg-zinc-950 border border-cyan-900/30 rounded-lg px-3 py-2 text-white text-xs focus:outline-none focus:border-cyan-500 resize-none"
                          data-testid={`candidate-message-${app.id}`} />
                        <button
                          onClick={() => sendCandidateMessage(app.id)}
                          disabled={sendingCandidateMsg === app.id}
                          className="mt-2 w-full flex items-center justify-center gap-2 py-2 bg-cyan-600 hover:bg-cyan-500 disabled:opacity-60 text-white font-semibold text-xs rounded-lg transition-colors"
                          data-testid={`send-candidate-message-${app.id}`}>
                          {sendingCandidateMsg === app.id ? 'Envoi…' : '📩 Envoyer le message en MP'}
                        </button>
                      </div>
                      <div className="mb-4">
                        <label className="block text-xs text-gray-400 mb-1.5">Note pour le candidat (optionnel)</label>
                        <div className="flex gap-2">
                          <input type="text"
                            placeholder="Ex: Profil intéressant, nous te contacterons..."
                            value={helperNotes[app.id] !== undefined ? helperNotes[app.id] : (app.admin_note || '')}
                            onChange={(e) => setHelperNotes(prev => ({ ...prev, [app.id]: e.target.value }))}
                            className="flex-1 bg-zinc-950 border border-cyan-900/30 rounded-lg px-3 py-2 text-white text-xs focus:outline-none focus:border-cyan-500"
                            data-testid={`helper-note-${app.id}`} />
                          <button onClick={() => updateHelperApplication(app.id, undefined, helperNotes[app.id] ?? app.admin_note ?? '')} className="px-3 py-2 bg-zinc-700 hover:bg-zinc-600 text-white text-xs rounded-lg transition-colors">Sauv.</button>
                        </div>
                      </div>
                      {app.status === 'pending' && (
                        <div className="flex gap-2">
                          <button onClick={() => updateHelperApplication(app.id, 'approved', helperNotes[app.id] ?? app.admin_note ?? '')}
                            className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-green-600 hover:bg-green-500 text-white font-semibold text-sm rounded-lg transition-colors"
                            data-testid={`approve-helper-${app.id}`}>
                            <CheckCircle className="w-4 h-4" /> Accepter
                          </button>
                          <button onClick={() => updateHelperApplication(app.id, 'rejected', helperNotes[app.id] ?? app.admin_note ?? '')}
                            className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-red-600/80 hover:bg-red-600 text-white font-semibold text-sm rounded-lg transition-colors"
                            data-testid={`reject-helper-${app.id}`}>
                            <XCircle className="w-4 h-4" /> Refuser
                          </button>
                        </div>
                      )}
                      {app.status !== 'pending' && (
                        <button onClick={() => updateHelperApplication(app.id, 'pending')} className="text-xs text-gray-400 hover:text-cyan-400 transition-colors">Remettre en attente</button>
                      )}
                    </div>
                  );
                })}
              </div>
              )}
              </>
            )}
          </div>
        )}

      </div>

      {/* Pay Affiliate Modal with proof upload */}
      {payModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={() => !paySubmitting && setPayModal(null)}
          data-testid="pay-affiliate-modal"
        >
          <div
            className="w-full max-w-lg bg-gray-900 border border-gray-800 rounded-2xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 border-b border-gray-800 flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-wider text-cyan-400">Paiement affilié</div>
                <div className="text-lg font-semibold text-white mt-1">
                  {payModal.username} — <span className="text-green-400">{payModal.amount.toFixed(2)}€</span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => !paySubmitting && setPayModal(null)}
                className="text-gray-400 hover:text-white"
                data-testid="pay-affiliate-close-btn"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm text-gray-300 mb-2">
                  Preuve de paiement (PNG / JPG, max 4 Mo)
                </label>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/webp"
                  onChange={handleProofFileChange}
                  className="block w-full text-sm text-gray-400 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-cyan-600/20 file:text-cyan-300 hover:file:bg-cyan-600/30 file:cursor-pointer"
                  data-testid="pay-affiliate-proof-input"
                />
                {payProofPreview && (
                  <div className="mt-3 rounded-lg overflow-hidden border border-gray-800">
                    <img
                      src={payProofPreview}
                      alt="Aperçu preuve"
                      className="w-full max-h-72 object-contain bg-black"
                      data-testid="pay-affiliate-proof-preview"
                    />
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-2">
                  Capture d'écran du virement / paiement. L'affilié pourra la voir depuis son espace.
                </p>
              </div>

              <div>
                <label className="block text-sm text-gray-300 mb-2">Note (optionnelle)</label>
                <textarea
                  value={payProofNote}
                  onChange={(e) => setPayProofNote(e.target.value.slice(0, 500))}
                  rows={2}
                  placeholder="Ex: virement PayPal — ref #1234"
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500"
                  data-testid="pay-affiliate-note-input"
                />
              </div>
            </div>

            <div className="p-5 border-t border-gray-800 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => !paySubmitting && setPayModal(null)}
                className="px-4 py-2 rounded-lg text-sm bg-gray-800 hover:bg-gray-700 text-gray-200"
                disabled={paySubmitting}
                data-testid="pay-affiliate-cancel-btn"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={submitPayAffiliate}
                disabled={paySubmitting}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-green-600 hover:bg-green-500 text-white disabled:opacity-50 flex items-center gap-2"
                data-testid="pay-affiliate-submit-btn"
              >
                {paySubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Envoi...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Confirmer le paiement
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Affiliate Details Modal */}
      {detailsModal && (
        <div
          className="fixed inset-0 z-[95] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={() => setDetailsModal(null)}
          data-testid="affiliate-details-modal"
        >
          <div
            className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-gray-900 border border-gray-800 rounded-2xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 border-b border-gray-800 flex items-center justify-between sticky top-0 bg-gray-900 z-10">
              <div className="flex items-center gap-3">
                {detailsModal.user?.avatar && detailsModal.user?.discord_id ? (
                  <img
                    src={`https://cdn.discordapp.com/avatars/${detailsModal.user.discord_id}/${detailsModal.user.avatar}.png`}
                    alt=""
                    className="w-10 h-10 rounded-full"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-purple-600/30 flex items-center justify-center">
                    <Users className="w-5 h-5 text-purple-400" />
                  </div>
                )}
                <div>
                  <div className="text-xs uppercase tracking-wider text-cyan-400">Affilié</div>
                  <div className="text-lg font-semibold text-white">
                    {detailsModal.user?.username || detailsModal.code}
                    <span className="ml-2 text-xs text-gray-500 font-mono">#{detailsModal.code}</span>
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setDetailsModal(null)}
                className="text-gray-400 hover:text-white"
                data-testid="affiliate-details-close-btn"
              >
                <XIcon className="w-6 h-6" />
              </button>
            </div>

            {detailsLoading || detailsModal.loading ? (
              <div className="p-10 flex items-center justify-center text-gray-400">
                <div className="w-6 h-6 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin mr-3"></div>
                Chargement...
              </div>
            ) : (
              <div className="p-5 space-y-5">
                {/* Affiliate link */}
                <div>
                  <label className="block text-xs uppercase tracking-wider text-gray-400 mb-2 flex items-center gap-2">
                    <LinkIcon className="w-3.5 h-3.5" />
                    Lien d'affiliation
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      readOnly
                      value={`${window.location.origin}/${detailsModal.code}`}
                      className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white font-mono"
                      data-testid="affiliate-details-link"
                    />
                    <button
                      type="button"
                      onClick={() => copyAffiliateLink(detailsModal.code)}
                      className="px-3 py-2 rounded-lg text-xs font-medium bg-cyan-600/20 hover:bg-cyan-600 text-cyan-300 hover:text-white transition-colors flex items-center gap-1.5 whitespace-nowrap"
                      data-testid="affiliate-details-copy-btn"
                    >
                      {detailsCopied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      {detailsCopied ? 'Copié' : 'Copier'}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setEditingLink(true); setNewLinkCode(detailsModal.code); }}
                      className="px-3 py-2 rounded-lg text-xs font-medium bg-purple-600/20 hover:bg-purple-600 text-purple-300 hover:text-white transition-colors flex items-center gap-1.5 whitespace-nowrap"
                      data-testid="affiliate-details-edit-link-btn"
                    >
                      <Edit3 className="w-4 h-4" />
                      Modifier
                    </button>
                  </div>
                  {editingLink && (
                    <div className="mt-2 bg-gray-800/60 border border-purple-500/30 rounded-lg p-3" data-testid="affiliate-rename-box">
                      <p className="text-[11px] text-gray-400 mb-2">
                        Nouveau nom du lien (ex: le nom de la structure). L'affilié, ses stats, commissions et code promo restent identiques.
                      </p>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500 font-mono">{window.location.origin}/</span>
                        <input
                          type="text"
                          value={newLinkCode}
                          onChange={(e) => setNewLinkCode(e.target.value.replace(/[^A-Za-z0-9_-]/g, ''))}
                          maxLength={32}
                          placeholder="NomStructure"
                          className="flex-1 bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white font-mono"
                          data-testid="affiliate-rename-input"
                        />
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <button
                          type="button"
                          onClick={renameAffiliateLink}
                          disabled={savingLink || !newLinkCode.trim() || newLinkCode.trim() === detailsModal.code}
                          className="px-3 py-1.5 rounded-lg text-xs font-medium bg-green-600 hover:bg-green-500 disabled:opacity-40 disabled:cursor-not-allowed text-white transition-colors"
                          data-testid="affiliate-rename-save-btn"
                        >
                          {savingLink ? 'Enregistrement...' : 'Enregistrer le lien'}
                        </button>
                        <button
                          type="button"
                          onClick={() => { setEditingLink(false); setNewLinkCode(''); }}
                          className="px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-700 hover:bg-gray-600 text-gray-200 transition-colors"
                          data-testid="affiliate-rename-cancel-btn"
                        >
                          Annuler
                        </button>
                      </div>
                    </div>
                  )}
                  <p className="text-[11px] text-gray-500 mt-1.5">
                    Code: <span className="font-mono text-purple-400">{detailsModal.code}</span> • Taux: <span className="text-purple-400">{detailsModal.commission_rate}%</span>
                  </p>
                </div>

                {/* Stats grid */}
                <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                  <div className="bg-black/30 rounded-lg p-3 text-center">
                    <p className="text-[10px] text-gray-400 uppercase">Clics</p>
                    <p className="text-lg font-bold text-cyan-400" data-testid="affiliate-details-clicks">{detailsModal.clicks || 0}</p>
                    {(detailsModal.clicks || 0) > 0 && (
                      <p className="text-[10px] text-gray-500 mt-0.5">
                        Conv. {(((detailsModal.total_sales || 0) / (detailsModal.clicks || 1)) * 100).toFixed(1)}%
                      </p>
                    )}
                  </div>
                  <div className="bg-black/30 rounded-lg p-3 text-center">
                    <p className="text-[10px] text-gray-400 uppercase">Connexions</p>
                    <p className="text-lg font-bold text-emerald-400" data-testid="affiliate-details-signups">{detailsModal.signups || 0}</p>
                    <p className="text-[10px] text-gray-500 mt-0.5">arrivées Discord</p>
                  </div>
                  <div className="bg-black/30 rounded-lg p-3 text-center">
                    <p className="text-[10px] text-gray-400 uppercase">Ventes</p>
                    <p className="text-lg font-bold text-white">{detailsModal.total_sales || 0}</p>
                  </div>
                  <div className="bg-black/30 rounded-lg p-3 text-center">
                    <p className="text-[10px] text-gray-400 uppercase">Revenu</p>
                    <p className="text-lg font-bold text-white">{(detailsModal.total_revenue || 0).toFixed(0)}€</p>
                  </div>
                  <div className="bg-black/30 rounded-lg p-3 text-center">
                    <p className="text-[10px] text-gray-400 uppercase">Payé</p>
                    <p className="text-lg font-bold text-green-400">{(detailsModal.paid_commission || 0).toFixed(2)}€</p>
                  </div>
                  <div className="bg-black/30 rounded-lg p-3 text-center">
                    <p className="text-[10px] text-gray-400 uppercase">À payer</p>
                    <p className="text-lg font-bold text-orange-400">{(detailsModal.pending_commission || 0).toFixed(2)}€</p>
                  </div>
                </div>

                {/* Payment history */}
                <div>
                  <h4 className="text-white font-semibold mb-2 flex items-center gap-2 text-sm">
                    <DollarSign className="w-4 h-4 text-green-400" />
                    Historique des paiements
                    <span className="text-gray-500 text-xs font-normal">({(detailsModal.payment_history || []).length})</span>
                  </h4>
                  {(!detailsModal.payment_history || detailsModal.payment_history.length === 0) ? (
                    <p className="text-sm text-gray-500 italic">Aucun paiement effectué</p>
                  ) : (
                    <div className="space-y-2">
                      {detailsModal.payment_history.map((p, i) => (
                        <div
                          key={p.id || i}
                          className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 bg-black/30 rounded-lg p-3"
                          data-testid={`admin-payment-row-${i}`}
                        >
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <CheckCircle className="w-4 h-4 text-green-400 shrink-0" />
                            <div className="min-w-0">
                              <p className="text-sm text-white">
                                {p.date ? new Date(p.date).toLocaleString('fr-FR') : 'Date inconnue'}
                              </p>
                              <p className="text-xs text-gray-500">
                                par {p.paid_by || 'admin'}
                                {p.proof_note ? ` • « ${p.proof_note} »` : ''}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 md:justify-end">
                            <span className="text-green-400 font-semibold whitespace-nowrap">+{(p.amount || 0).toFixed(2)}€</span>
                            {p.proof_id ? (
                              <button
                                onClick={() => openAdminProof(p.proof_id)}
                                disabled={detailsProofLoadingId === p.proof_id}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-cyan-600/20 hover:bg-cyan-600 text-cyan-300 hover:text-white transition-colors disabled:opacity-50"
                                data-testid={`admin-view-proof-btn-${i}`}
                              >
                                {detailsProofLoadingId === p.proof_id ? (
                                  <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                                ) : (
                                  <ImageIcon className="w-3.5 h-3.5" />
                                )}
                                Preuve
                              </button>
                            ) : (
                              <span className="text-gray-600 text-[11px] italic">sans preuve</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Recent sales (compact) */}
                {detailsModal.recent_sales && detailsModal.recent_sales.length > 0 && (
                  <div>
                    <h4 className="text-white font-semibold mb-2 flex items-center gap-2 text-sm">
                      <Gift className="w-4 h-4 text-purple-400" />
                      Dernières ventes
                      <span className="text-gray-500 text-xs font-normal">({detailsModal.recent_sales.length})</span>
                    </h4>
                    <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
                      {detailsModal.recent_sales.map((s, i) => (
                        <div key={i} className="flex items-center justify-between bg-black/20 rounded px-3 py-2 text-xs">
                          <span className="text-gray-400">
                            {s.date ? new Date(s.date).toLocaleDateString('fr-FR') : '—'}
                          </span>
                          <span className="text-green-400 font-medium">+{(s.commission || 0).toFixed(2)}€</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Admin Proof Viewer */}
      {detailsProof && (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4"
          onClick={() => setDetailsProof(null)}
          data-testid="admin-proof-viewer-modal"
        >
          <div
            className="relative max-w-3xl w-full bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-zinc-800">
              <div>
                <p className="text-white font-semibold text-sm">Preuve de paiement</p>
                {detailsProof.created_at && (
                  <p className="text-gray-500 text-xs">
                    {new Date(detailsProof.created_at).toLocaleString('fr-FR')}
                    {detailsProof.uploaded_by ? ` • par ${detailsProof.uploaded_by}` : ''}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => setDetailsProof(null)}
                className="text-gray-400 hover:text-white"
                data-testid="admin-proof-viewer-close-btn"
              >
                <XIcon className="w-6 h-6" />
              </button>
            </div>
            <div className="bg-black">
              <img
                src={`data:${detailsProof.content_type || 'image/png'};base64,${detailsProof.image_base64}`}
                alt="Preuve de paiement"
                className="w-full max-h-[75vh] object-contain"
                data-testid="admin-proof-viewer-image"
              />
            </div>
            {detailsProof.note && (
              <div className="p-4 border-t border-zinc-800 text-sm text-gray-300 flex gap-2">
                <FileText className="w-4 h-4 text-cyan-400 mt-0.5 shrink-0" />
                <span>{detailsProof.note}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};


export default AdminPage;
